"use client";

import Link from "next/link";
import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useDefaultLanguage, usePageUrls } from "@/contexts/PageUrlsContext";
import { buildProductDetailUrl, resolveReservationBookNowUrl } from "@/lib/url-helpers";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { RESERVATION_CHECKOUT_COOKIE_NAME, RESERVATION_PRICING_STORAGE_KEY, clearReservationCookies } from "@/lib/cookies";
import { markBfcacheHardRefreshOnNextRestore } from "@/lib/bfcache";
import NationalitySelect from "@/components/ui/NationalitySelect";
import paymentService, {
  type BankAccount,
  type CardCommissionQuote,
  type InstallmentOption,
  fetchCardCommissionQuote,
} from "@/services/PaymentService";
import AgreementModal from "@/components/ui/AgreementModal";
import {
  PaymentMethodSection,
  PartialPaymentAmountBlock,
  PaymentSchedulePlanner,
  validatePaymentScheduleRows,
  type PaymentScheduleRow,
} from "@/components/payment";
import { fetchPartialPaymentPolicy } from "@/services/PartialPaymentPolicyService";
import {
  computeGrandTotalWithLateFees,
  computeScheduleLateFees,
} from "@/lib/partial-payment-schedule";
import type { PartialPaymentBounds, PartialPaymentPolicy } from "@/types/partial-payment";
import type { Room } from "@/types/travelers";
import {
  loadRecaptchaScript,
  renderRecaptchaWidget,
  resetRecaptchaWidget,
  getRecaptchaToken,
  loadRecaptchaV3Script,
  getRecaptchaV3Token,
  loadTurnstileScript,
  renderTurnstileWidget,
  resetTurnstileWidget,
  getTurnstileToken,
  removeTurnstileWidget,
} from "@/lib/captcha";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import CountryCitySelect from "@/components/ui/CountryCitySelect";
import { useCountries } from "@/contexts/CountriesContext";

/** Rezervasyon formu sözleşmesi (forms/details agreements ile uyumlu) */
interface Agreement {
  id: number;
  title: string;
  slug: string;
  is_required: boolean;
  content?: string;
}

const TURKEY_CODE = "TR";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function bookNowFieldClass(hasError?: boolean): string {
  return [
    "w-full px-3 py-2.5 border rounded-lg bg-white transition-colors",
    "focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first",
    hasError ? "border-red-400 ring-1 ring-red-200" : "border-gray-200",
  ].join(" ");
}

type BookNowFieldErrors = Record<string, string>;
const RESERVATION_COOKIE_MAX_AGE_DAYS = 7;

/** Book Now sayfasında tekrarlayan API isteklerini azaltır (Strict Mode / çift mount). */
const BOOK_NOW_FETCH_CACHE_TTL_MS = 60 * 1000; // 1 dk
const bookNowFetchCache = new Map<string, { data: unknown; at: number }>();

async function getCachedOrFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = bookNowFetchCache.get(key);
  if (cached && Date.now() - cached.at < BOOK_NOW_FETCH_CACHE_TTL_MS) {
    return cached.data as T;
  }
  const data = await fetcher();
  bookNowFetchCache.set(key, { data, at: Date.now() });
  return data;
}
/** Oda başına max yetişkin (RoomTravelersSelector / types/travelers ile uyumlu) */
const MAX_ADULTS_PER_ROOM = 3;
/** Oda başına max kişi (yetişkin + çocuk); 3 yetişkin ise çocuk eklenemez */
const MAX_PERSONS_PER_ROOM = 3;

/** Odayı geçerli kapasiteye getirir: yetişkin 1–3, çocuk 0..(3-adults), childAges uzunluğu children kadar */
function normalizeRoom(room: Room): Room {
  const adults = Math.max(1, Math.min(MAX_ADULTS_PER_ROOM, room.adults ?? 1));
  const maxChildren = adults === MAX_ADULTS_PER_ROOM ? 0 : MAX_PERSONS_PER_ROOM - adults;
  const children = Math.max(0, Math.min(maxChildren, room.children ?? 0));
  const childAges = (room.childAges ?? []).slice(0, children);
  return { adults, children, childAges };
}

function roomPax(room: Room): number {
  return (room.adults ?? 0) + (room.children ?? 0);
}

function totalPaxFromRooms(rooms: Room[]): number {
  return rooms.reduce((sum, r) => sum + roomPax(r), 0);
}

/** Çıkış kontenjanı / max grup: toplam yolcu sayısını aşmayacak şekilde oda listesini kısaltır */
function clampRoomsToMaxTravelers(rooms: Room[], maxTotal: number): Room[] {
  if (maxTotal <= 0) {
    return [{ adults: 1, children: 0, childAges: [] }];
  }
  let list = rooms.length > 0 ? rooms.map(normalizeRoom) : [{ adults: 1, children: 0, childAges: [] }];

  const shrinkOnce = (): boolean => {
    if (totalPaxFromRooms(list) <= maxTotal) return false;
    for (let i = list.length - 1; i >= 0; i--) {
      if (totalPaxFromRooms(list) <= maxTotal) break;
      const r = { ...list[i] };
      if ((r.children ?? 0) > 0) {
        r.children = (r.children ?? 0) - 1;
        r.childAges = (r.childAges ?? []).slice(0, r.children);
        list[i] = normalizeRoom(r);
        return true;
      }
      if ((r.adults ?? 1) > 1) {
        r.adults = (r.adults ?? 1) - 1;
        list[i] = normalizeRoom(r);
        return true;
      }
      if (list.length > 1) {
        list.splice(i, 1);
        return true;
      }
    }
    return false;
  };

  let guard = 0;
  while (totalPaxFromRooms(list) > maxTotal && guard < 200) {
    if (!shrinkOnce()) break;
    guard += 1;
  }

  if (maxTotal === 1 && list.length > 1) {
    list = [{ adults: 1, children: 0, childAges: [] }];
  }

  return list.map(normalizeRoom);
}

/** Çıkış tarihini Y-m-d formatına getirir (dönem eşlemesi için) */
function normalizeDepartureDate(dateStr: string | null | undefined): string {
  if (!dateStr || typeof dateStr !== "string") return "-";
  const s = dateStr.trim();
  const ymd = /^(\d{4}-\d{2}-\d{2})/.exec(s)?.[1];
  if (ymd) return ymd;
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
  } catch {
    // ignore
  }
  return s.slice(0, 10) || "-";
}

/** Grup tur fiyatı: çıkış tarihine göre dönem, oda bazlı: 1 kişi = single, 2 = 2×perPerson, 3 = 2×perPerson + extraBed + childRules */
interface TourPricingRow {
  perPersonDouble?: number | null;
  singleRoom?: number | null;
  extraBed?: number | null;
  childrenFee?: number | null;
  childRules?: Array<{ min_age: number; max_age: number; fee: number }>;
}
interface TourPricingPeriod {
  periodType?: string;
  periodStart?: string;
  periodEnd?: string;
  rows?: TourPricingRow[];
}

/** API'den gelen tour_pricing snake_case veya camelCase olabilir; hesaplama için tek formata getirir */
function normalizeTourPricing(periods: unknown): TourPricingPeriod[] {
  if (!Array.isArray(periods)) return [];
  return periods.map((p: Record<string, unknown>) => {
    const period = p as TourPricingPeriod & { period_type?: string; period_start?: string; period_end?: string };
    const rows = (period.rows ?? (p as { rows?: unknown[] }).rows) ?? [];
    return {
      periodType: (period.periodType ?? period.period_type ?? "group") as string,
      periodStart: (period.periodStart ?? period.period_start ?? "").toString().trim(),
      periodEnd: (period.periodEnd ?? period.period_end ?? "").toString().trim(),
      rows: (rows as Record<string, unknown>[]).map((r) => {
        const row = r as TourPricingRow & {
          per_person_double?: number | null;
          price_per_person?: number | null;
          single_room?: number | null;
          single_supplement?: number | null;
          extra_bed?: number | null;
          extra_bed_price?: number | null;
          children_fee?: number | null;
          child_rules?: TourPricingRow["childRules"];
        };
        return {
          perPersonDouble:
            row.perPersonDouble ?? row.per_person_double ?? row.price_per_person ?? 0,
          singleRoom: row.singleRoom ?? row.single_room ?? row.single_supplement ?? null,
          extraBed: row.extraBed ?? row.extra_bed ?? row.extra_bed_price ?? 0,
          childrenFee: row.childrenFee ?? row.children_fee ?? 0,
          childRules: (row.childRules ?? row.child_rules ?? []) as TourPricingRow["childRules"],
        };
      }),
    };
  });
}

/** Oda bazlı fiyat satırı (Price breakdown'da gösterim için) */
interface RoomPriceLine {
  type: "per_person" | "single" | "extra_bed" | "child";
  label: string;
  count: number;
  unitPrice: number;
  amount: number;
  ageRange?: string;
}

interface GroupPriceResult {
  total_price: number;
  roomPrices: Array<{ roomIndex: number; total_price: number }>;
  roomBreakdowns: Array<{ roomIndex: number; total_price: number; lines: RoomPriceLine[] }>;
  periodUnitPrices: { perPerson: number; singleRoom: number | null; extraBed: number; childrenFee: number; childRules: TourPricingRow["childRules"] };
  currency?: { symbol?: string; code?: string };
}

function calculateGroupPriceFromTourPricing(
  tourPricing: TourPricingPeriod[],
  dateStr: string,
  rooms: Array<{ adults: number; children: number; childAges: number[] }>,
  currency?: { symbol?: string; code?: string }
): GroupPriceResult | null {
  const normalizedDate = dateStr === "-" ? "" : normalizeDepartureDate(dateStr);
  const groupPeriods = (tourPricing ?? []).filter((p) => (p.periodType ?? "group") === "group");
  const privatePeriods = (tourPricing ?? []).filter((p) => (p.periodType ?? "private") === "private");
  const matchDate = (list: TourPricingPeriod[]) =>
    list.filter((p) => {
      const start = (p.periodStart ?? "").trim();
      const end = (p.periodEnd ?? "").trim();
      if (!start || !end) return true;
      return normalizedDate >= start && normalizedDate <= end;
    });
  const period =
    matchDate(groupPeriods)[0] ?? groupPeriods[0] ?? matchDate(privatePeriods)[0] ?? privatePeriods[0];
  if (!period?.rows?.length) return null;
  const row = period.rows[0];
  const perPerson = row.perPersonDouble ?? 0;
  const singleRoom = row.singleRoom ?? null;
  const extraBed = row.extraBed ?? 0;
  const childRules = row.childRules ?? [];
  const fallbackChildFee = row.childrenFee ?? 0;

  const roomPrices: Array<{ roomIndex: number; total_price: number }> = [];
  const roomBreakdowns: GroupPriceResult["roomBreakdowns"] = [];
  let totalAmount = 0;

  for (let i = 0; i < rooms.length; i++) {
    const room = rooms[i];
    const adults = Math.max(0, room.adults ?? 0);
    const childAges = room.childAges ?? [];
    const lines: RoomPriceLine[] = [];
    let roomTotal = 0;

    if (adults === 1 && singleRoom != null) {
      roomTotal += singleRoom;
      lines.push({ type: "single", label: "Single supplement", count: 1, unitPrice: singleRoom, amount: singleRoom });
    } else if (adults === 2) {
      const amount = perPerson * 2;
      roomTotal += amount;
      lines.push({ type: "per_person", label: "Per person (double)", count: 2, unitPrice: perPerson, amount });
    } else if (adults >= 3) {
      const doublePart = perPerson * 2;
      const extraBedCount = adults - 2;
      const extraBedAmount = extraBed * extraBedCount;
      roomTotal += doublePart + extraBedAmount;
      lines.push({ type: "per_person", label: "Per person (double)", count: 2, unitPrice: perPerson, amount: doublePart });
      lines.push({ type: "extra_bed", label: "Extra bed", count: extraBedCount, unitPrice: extraBed, amount: extraBedAmount });
    } else if (adults > 0) {
      const amount = perPerson * adults;
      roomTotal += amount;
      lines.push({ type: "per_person", label: "Per person", count: adults, unitPrice: perPerson, amount });
    }

    for (const age of childAges) {
      const rule = childRules.find((r) => age >= r.min_age && age <= r.max_age);
      const fee = rule ? rule.fee : fallbackChildFee;
      roomTotal += fee;
      const ageRange = rule ? `${rule.min_age}-${rule.max_age}` : "";
      lines.push({
        type: "child",
        label: ageRange ? `Child (age ${ageRange})` : "Child",
        count: 1,
        unitPrice: fee,
        amount: fee,
        ageRange: ageRange || undefined,
      });
    }

    roomPrices.push({ roomIndex: i, total_price: roomTotal });
    roomBreakdowns.push({ roomIndex: i, total_price: roomTotal, lines });
    totalAmount += roomTotal;
  }

  return {
    total_price: totalAmount,
    roomPrices,
    roomBreakdowns,
    periodUnitPrices: {
      perPerson,
      singleRoom,
      extraBed,
      childrenFee: fallbackChildFee,
      childRules: childRules.length ? childRules : undefined,
    },
    currency,
  };
}

/** İndirimli kişi başı fiyatı uygula: base result'taki sadece per_person kalemlerini scale et, single/extra_bed/child aynı kalsın */
function applyDiscountedPerPersonToGroupResult(
  base: GroupPriceResult,
  discountedPerPerson: number
): GroupPriceResult {
  const originalPerPerson = base.periodUnitPrices.perPerson;
  if (originalPerPerson <= 0 || Math.abs(discountedPerPerson - originalPerPerson) < 0.01) return base;
  const scale = discountedPerPerson / originalPerPerson;

  const newRoomBreakdowns: GroupPriceResult["roomBreakdowns"] = base.roomBreakdowns.map((rb) => {
    let newRoomTotal = 0;
    const newLines: RoomPriceLine[] = rb.lines.map((line) => {
      const amount = line.type === "per_person" ? line.amount * scale : line.amount;
      newRoomTotal += amount;
      return { ...line, amount, unitPrice: line.type === "per_person" ? (line.unitPrice * scale) : line.unitPrice };
    });
    return { roomIndex: rb.roomIndex, total_price: newRoomTotal, lines: newLines };
  });

  const newRoomPrices: GroupPriceResult["roomPrices"] = newRoomBreakdowns.map((rb) => ({
    roomIndex: rb.roomIndex,
    total_price: rb.total_price,
  }));

  const newTotal = newRoomPrices.reduce((s, r) => s + r.total_price, 0);

  return {
    total_price: newTotal,
    roomPrices: newRoomPrices,
    roomBreakdowns: newRoomBreakdowns,
    periodUnitPrices: {
      ...base.periodUnitPrices,
      perPerson: discountedPerPerson,
    },
    currency: base.currency,
  };
}

interface ReservationBookNowProps {
  language?: string;
  translations?: Record<string, string>;
  /** Sayfa yüklenirken önceden çekilmiş grup fiyat dönemleri; verilirse ek product fetch sadece metas için yapılır, fiyat bu veriden hesaplanır */
  initialTourPricing?: TourPricingPeriod[] | null;
  /** Sayfa verisinden gelen para birimi (initialTourPricing ile birlikte kullanılır) */
  initialProductCurrency?: { symbol?: string; code?: string } | null;
}

/** Çerezdeki rezervasyon özeti tipi (URL parametresi kullanılmaz; ROADMAP: veriler çerez/session ile taşınır) */
interface CookieSummary {
  flow?: string;
  product_id?: number;
  product_slug?: string;
  product_name?: string;
  product_type?: string;
  travel_date?: string;
  check_in_date?: string;
  rooms?: Array<{ adults?: number; children?: number; childAges?: number[] }>;
  guest_count?: number;
  child_count?: number;
  period_id?: number;
  currency?: { symbol?: string; code?: string };
  total_price?: number;
  room_prices?: Array<{ roomIndex?: number; total_price?: number; currency?: { symbol?: string } }>;
  /** Tur detaydan book-now’a giderken kaydedilen tam yol (dil prefix + hash dahil) */
  return_path?: string;
}

interface ParticipantField {
  type: "adult" | "child";
  first_name: string;
  last_name: string;
  national_id: string;
  passport_no: string;
  nationality: string;
  birth_date?: string;
  save_for_future?: boolean;
}

function buildParticipantSlots(rooms: CookieSummary["rooms"]): ParticipantField[][] {
  if (!rooms?.length) return [];
  return rooms.map((room) => {
    const slots: ParticipantField[] = [];
    const adults = room.adults ?? 0;
    const children = room.children ?? 0;
    for (let i = 0; i < adults; i++) {
      slots.push({ type: "adult", first_name: "", last_name: "", national_id: "", passport_no: "", nationality: "", save_for_future: false });
    }
    for (let i = 0; i < children; i++) {
      slots.push({ type: "child", first_name: "", last_name: "", national_id: "", passport_no: "", nationality: "", birth_date: "", save_for_future: false });
    }
    return slots;
  });
}

export default function ReservationBookNow({
  language = "en",
  translations = {},
  initialTourPricing = null,
  initialProductCurrency = null,
}: ReservationBookNowProps) {
  const router = useRouter();
  const { getVariable, language: currentLocale } = useLanguageVariables();
  const pageUrls = usePageUrls();
  const defaultLanguage = useDefaultLanguage() ?? "en";
  const bookNowPageUrl = useMemo(
    () => resolveReservationBookNowUrl(pageUrls, language, defaultLanguage),
    [pageUrls, language, defaultLanguage],
  );
  const { customer, refreshCustomer, login: authLogin, isAuthenticated } = useCustomerAuth();
  const countries = useCountries();
  const contactPrefilledRef = useRef(false);
  const loginModalWasOpenRef = useRef(false);
  const contactSectionRef = useRef<HTMLDivElement>(null);
  const billingSectionRef = useRef<HTMLDivElement>(null);
  const participantsSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const [cookieSummary, setCookieSummary] = useState<CookieSummary | null>(null);

  useEffect(() => {
    markBfcacheHardRefreshOnNextRestore();
  }, []);

  // URL'de rezervasyon parametreleri varsa temizle (veriler sadece çerezde; ROADMAP)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasParams = window.location.search && window.location.search.length > 1;
    if (hasParams && cookieSummary) {
      window.history.replaceState(null, "", bookNowPageUrl);
    }
  }, [bookNowPageUrl, cookieSummary]);

  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", national_id: "", passport_no: "", nationality: "" });
  /** Fatura üzerindeki ad/e-posta iletişimden farklıysa */
  const [billingContactDifferent, setBillingContactDifferent] = useState(false);
  const [billingContact, setBillingContact] = useState({ first_name: "", last_name: "", email: "" });
  const [billing, setBilling] = useState({
    address: "",
    city: "",
    country: "",
    postal_code: "",
  });
  const [fieldErrors, setFieldErrors] = useState<BookNowFieldErrors>({});
  /** Oda/yolcu seçimi (TourRadar tarzı); çerezden ilk doldurulur, kullanıcı değiştirebilir */
  const [rooms, setRooms] = useState<Room[]>([]);
  const [participantsByRoom, setParticipantsByRoom] = useState<ParticipantField[][]>([]);
  const [productMetas, setProductMetas] = useState<{ min_group_size?: number; max_group_size?: number; min_ages?: string | number | null; child_pax_age_threshold_private?: number } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"card" | "bank">("card");
  const [cardHolder, setCardHolder] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expireMonth, setExpireMonth] = useState("");
  const [expireYear, setExpireYear] = useState("");
  const [cvc, setCvc] = useState("");
  const [use3D, setUse3D] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threeDSHtml, setThreeDSHtml] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successKind, setSuccessKind] = useState<"card" | "bank" | null>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalEmail, setLoginModalEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  /** Rezervasyon formu sözleşmeleri (forms/details'ten) */
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [reservationFormLanguageId, setReservationFormLanguageId] = useState<number | undefined>();
  const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>({});
  const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  /** Ürün tour_pricing (sayfa yüklenirken tek product fetch ile alınır; client-side fiyat için) */
  const [productTourPricing, setProductTourPricing] = useState<TourPricingPeriod[] | null>(null);
  /** Ürün para birimi (product.currency) */
  const [productCurrency, setProductCurrency] = useState<{ symbol?: string; code?: string } | null>(null);
  /** Book Now sayfasında seçilen çıkış tarihi (null = çerezdeki değer kullanılır) */
  const [selectedDepartureDate, setSelectedDepartureDate] = useState<string | null>(null);
  /** API'den gelen uygun çıkış tarihleri (dropdown / ay bazlı takvim için) */
  const [availableDepartureDates, setAvailableDepartureDates] = useState<
    Array<{
      id?: number;
      departure_date: string;
      status?: string;
      discount_percentage?: number | null;
      remaining_pax?: number | null;
      is_bookable?: boolean;
      availability?: string;
    }>
  >([]);
  const [availableDepartureDatesLoading, setAvailableDepartureDatesLoading] = useState(false);
  /** Seçili ay (Y-m) – ay bazlı fiyatlı tarih listesi için */
  const [selectedMonthForCalendar, setSelectedMonthForCalendar] = useState<string>("");
  /** Seçili aydaki tarihlerin kişi başı fiyatları (API /prices); kampanya varsa original_price_per_person, applied_campaigns dahil */
  const [monthPrices, setMonthPrices] = useState<Array<{
    departure_date: string;
    price_per_person?: number | null;
    status?: string;
    discount_percentage?: number | null;
    original_price_per_person?: number | null;
    discount_amount?: number | null;
    applied_campaigns?: Array<{ name?: string }>;
  }>>([]);
  const [monthPricesLoading, setMonthPricesLoading] = useState(false);
  /** Ürün başlangıç/bitiş şehirleri (sidebar START/ENDS için) */
  const [productStartCity, setProductStartCity] = useState<string>("");
  const [productEndCity, setProductEndCity] = useState<string>("");
  const [tourDurationDays, setTourDurationDays] = useState<number | null>(null);
  /** SSR/hydration: çerez başlığı sadece mount sonrası gösterilir, böylece sunucu ve ilk client render aynı olur */
  const [displayProductName, setDisplayProductName] = useState("");
  /** Hydration: cookie/selection-dependent blokları sadece client mount sonrası göster; sunucu ve ilk client aynı çıktıyı üretsin */
  const [hasMounted, setHasMounted] = useState(false);
  /** Ödeme seçenekleri: test modu, banka hesapları (InvoicePayment ile aynı kaynak) */
  const [paymentOptions, setPaymentOptions] = useState<{
    test_mode?: boolean;
    payment_provider_key?: string;
    bank_accounts?: BankAccount[];
  } | null>(null);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);
  /** Kart komisyonu (şimdi ödenecek tutar ve varsa vade planı tahmini) */
  const [cardCommissionNow, setCardCommissionNow] = useState<CardCommissionQuote | null>(null);
  /** Banka havalesi formu (InvoicePayment ile aynı alanlar; ödeme sayfasına yönlendirildikten sonra bildirim orada yapılır) */
  const [selectedBank, setSelectedBank] = useState<number | null>(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderBank, setSenderBank] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [partialPaymentBounds, setPartialPaymentBounds] = useState<PartialPaymentBounds | null>(null);
  const [partialPaymentPolicy, setPartialPaymentPolicy] = useState<PartialPaymentPolicy | null>(null);
  /** Tur/policy kısmi ödemeye izin veriyor mu (API) */
  const [partialPaymentOffered, setPartialPaymentOffered] = useState(false);
  /** Kullanıcı "kısmi ödemek istiyorum" seçti mi */
  const [wantsPartialPayment, setWantsPartialPayment] = useState(false);
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);
  const [cardStorageAvailable, setCardStorageAvailable] = useState(false);
  const partialPaymentActive = partialPaymentOffered && wantsPartialPayment;
  const [payNowAmount, setPayNowAmount] = useState("");
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentScheduleRow[]>([]);
  const [installment, setInstallment] = useState(1);
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);

  const captchaWidgetIdRef = useRef<number | string | null>(null);
  /** handleRoomsChange içinde güncel yolcu üst sınırı (çıkış kontenjanı + max grup) */
  const effectiveMaxTravelersRef = useRef<number | null>(null);
  const { config: captchaConfig, isLoading: captchaConfigLoading } = useCaptchaConfig();
  const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);

  useEffect(() => {
    setHasMounted(true);
    try {
      const raw = document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${RESERVATION_CHECKOUT_COOKIE_NAME}=`));
      if (!raw) return;
      const decoded = decodeURIComponent(raw.split("=").slice(1).join("="));
      setCookieSummary(JSON.parse(decoded) as CookieSummary);
    } catch {
      setCookieSummary(null);
    }
  }, []);

  // Ödeme seçeneklerini getir – cache ile tekrarlayan istek önlenir
  useEffect(() => {
    setPaymentOptionsLoading(true);
    const key = "pay:payment-options";
    getCachedOrFetch(key, () => fetch("/api/pay/payment-options", { credentials: "include" }).then((res) => res.json()))
      .then((data: { success?: boolean; data?: { test_mode?: boolean; payment_provider_key?: string; bank_accounts?: unknown }; test_mode?: boolean; payment_provider_key?: string; bank_accounts?: unknown }) => {
        const payload = data?.data ?? data;
        const raw = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
        if (raw) {
          const bankList = Array.isArray(raw.bank_accounts)
            ? raw.bank_accounts
            : Array.isArray((data as Record<string, unknown>).bank_accounts)
              ? (data as Record<string, unknown>).bank_accounts
              : [];
          const providerKey =
            typeof raw.payment_provider_key === "string"
              ? raw.payment_provider_key
              : typeof (data as Record<string, unknown>).payment_provider_key === "string"
                ? ((data as Record<string, unknown>).payment_provider_key as string)
                : undefined;
          setPaymentOptions({
            test_mode: Boolean(raw.test_mode ?? (data as Record<string, unknown>).test_mode ?? false),
            payment_provider_key: providerKey,
            bank_accounts: bankList as BankAccount[],
          });
        } else {
          setPaymentOptions(null);
        }
      })
      .catch(() => setPaymentOptions(null))
      .finally(() => setPaymentOptionsLoading(false));
  }, []);

  useEffect(() => {
    const banks = paymentOptions?.bank_accounts ?? [];
    if (paymentMethod === "bank" && selectedBank == null && banks.length === 1) {
      setSelectedBank(banks[0]!.id);
    }
  }, [paymentMethod, paymentOptions, selectedBank]);

  const BOOK_NOW_CAPTCHA_CONTAINER_ID = "book-now-captcha-container";
  // Captcha: v2/Turnstile widget; v3/Enterprise için script ön yükle (widget yok)
  useEffect(() => {
    if (captchaConfigLoading || !captchaConfig?.is_enabled || !captchaConfig?.site_key || !captchaConfig?.provider) return;
    const provider = captchaConfig.provider;
    const siteKey = captchaConfig.site_key;
    const scriptUrl = captchaConfig.script_url;
    const initCaptcha = async () => {
      try {
        if (provider === "google_recaptcha_v2") {
          await loadRecaptchaScript(scriptUrl || undefined);
          const container = document.getElementById(BOOK_NOW_CAPTCHA_CONTAINER_ID);
          if (container && siteKey) {
            const id = await renderRecaptchaWidget(BOOK_NOW_CAPTCHA_CONTAINER_ID, siteKey, () => {});
            captchaWidgetIdRef.current = id;
          }
        } else if (provider === "google_recaptcha_v3" || provider === "google_recaptcha_enterprise") {
          await loadRecaptchaV3Script(siteKey, provider === "google_recaptcha_enterprise" ? scriptUrl ?? undefined : undefined);
        } else if (provider === "cloudflare_turnstile") {
          await loadTurnstileScript(scriptUrl || undefined);
          const container = document.getElementById(BOOK_NOW_CAPTCHA_CONTAINER_ID);
          if (container && siteKey) {
            const id = await renderTurnstileWidget(BOOK_NOW_CAPTCHA_CONTAINER_ID, siteKey, () => {});
            captchaWidgetIdRef.current = id;
          }
        }
      } catch {
        // ignore
      }
    };
    const t = setTimeout(initCaptcha, 500);
    return () => {
      clearTimeout(t);
      if (captchaWidgetIdRef.current != null && provider === "google_recaptcha_v2") {
        resetRecaptchaWidget(captchaWidgetIdRef.current as number);
      } else if (captchaWidgetIdRef.current != null && provider === "cloudflare_turnstile") {
        resetTurnstileWidget(captchaWidgetIdRef.current as string);
        removeTurnstileWidget(captchaWidgetIdRef.current as string);
      }
    };
  }, [captchaConfig, captchaConfigLoading]);

  /** Test kartı yükle (iyzico tarzı – Garanti dışı sağlayıcılar için) */
  const loadTestCard = useCallback(() => {
    const testCards = [
      { number: "5890040000000016", holder: "TEST KART", month: "12", year: "30", cvc: "123" },
      { number: "5526080000000006", holder: "TEST KART", month: "12", year: "30", cvc: "123" },
      { number: "4766620000000001", holder: "TEST KART", month: "12", year: "30", cvc: "123" },
    ];
    const card = testCards[Math.floor(Math.random() * testCards.length)];
    const futureYear = new Date().getFullYear() + 2;
    setCardNumber(card.number);
    setCardHolder(card.holder);
    setExpireMonth(card.month);
    setExpireYear(String(futureYear).slice(-2));
    setCvc(card.cvc);
  }, []);


  // Oturum açıksa iletişim bilgilerini hesap verisiyle doldur (sadece bir kez, kullanıcı değiştirmediyse)
  useEffect(() => {
    if (!customer || contactPrefilledRef.current) return;
    setForm((prev) => ({
      ...prev,
      first_name: prev.first_name.trim() || customer.first_name || prev.first_name,
      last_name: prev.last_name.trim() || customer.last_name || prev.last_name,
      email: prev.email.trim() || customer.email || prev.email,
      phone: prev.phone.trim() || (customer.phone ?? "") || prev.phone,
    }));
    contactPrefilledRef.current = true;
  }, [customer]);

  // Giriş popup'tan başarılı giriş sonrası formu güncelle
  useEffect(() => {
    if (loginModalWasOpenRef.current && customer) {
      setForm((prev) => ({
        ...prev,
        first_name: customer.first_name || prev.first_name,
        last_name: customer.last_name || prev.last_name,
        email: customer.email || prev.email,
        phone: customer.phone ?? prev.phone,
      }));
      loginModalWasOpenRef.current = false;
    }
  }, [customer]);

  /** Çerezden oda/yolcu sayısını yükle (bir kez); kapasite kurallarına normalize edilir */
  useEffect(() => {
    if (!cookieSummary?.rooms?.length || rooms.length > 0) return;
    const loaded: Room[] = cookieSummary.rooms.map((r) => ({
      adults: r.adults ?? 2,
      children: r.children ?? 0,
      childAges: Array.isArray(r.childAges) ? r.childAges : [],
    }));
    const normalized = loaded.map(normalizeRoom);
    setRooms(normalized);
    setParticipantsByRoom(buildParticipantSlots(normalized));
  }, [cookieSummary?.rooms, rooms.length]);

  /** İlk render: prop ile gelen fiyat dönemi varsa kullan (ek istek yok) */
  useEffect(() => {
    if (initialTourPricing != null) {
      setProductTourPricing(Array.isArray(initialTourPricing) ? initialTourPricing : null);
    }
    if (initialProductCurrency != null) {
      setProductCurrency(initialProductCurrency && typeof initialProductCurrency === "object" ? initialProductCurrency : null);
    }
  }, [initialTourPricing, initialProductCurrency]);

  /** Uygun çıkış tarihleri: product_id veya slug ile API'den – cache ile tekrar istek önlenir */
  useEffect(() => {
    const productIdNum = cookieSummary?.product_id;
    const slug = cookieSummary?.product_slug;
    if (!productIdNum && !slug) {
      setAvailableDepartureDates([]);
      return;
    }
    const params = new URLSearchParams();
    if (productIdNum) params.set("product_id", String(productIdNum));
    else if (slug) params.set("slug", slug);
    params.set("language", language ?? "en");
    const url = `/api/tour-departure-dates?${params.toString()}`;
    const key = `tour-departure-dates:v2:${productIdNum ?? ""}:${slug ?? ""}:${language ?? "en"}`;
    setAvailableDepartureDatesLoading(true);
    getCachedOrFetch(key, () => fetch(url).then((res) => res.json()))
      .then((data: {
        success?: boolean;
        data?: Array<{
          id?: number;
          departure_date?: string;
          status?: string;
          discount_percentage?: number | null;
          remaining_pax?: number | null;
          is_bookable?: boolean;
          availability?: string;
        }>;
      }) => {
        if (!data?.success || !Array.isArray(data.data)) {
          setAvailableDepartureDates([]);
          return;
        }
        const today = new Date().toISOString().slice(0, 10);
        const list = data.data
          .filter((d) => d?.departure_date && String(d.departure_date).slice(0, 10) >= today)
          .map((d) => ({
            id: d.id,
            departure_date: String(d.departure_date).slice(0, 10),
            status: d.status,
            discount_percentage: d.discount_percentage != null ? Number(d.discount_percentage) : null,
            remaining_pax: d.remaining_pax != null ? Number(d.remaining_pax) : null,
            is_bookable: d.is_bookable,
            availability: d.availability,
          }))
          .sort((a, b) => a.departure_date.localeCompare(b.departure_date))
          .slice(0, 120);
        setAvailableDepartureDates(list);
        if (list.length > 0) {
          const preferred = cookieSummary?.travel_date || cookieSummary?.check_in_date;
          const month =
            typeof preferred === "string" && preferred.length >= 7 && list.some((d) => d.departure_date === preferred)
              ? preferred.slice(0, 7)
              : list[0].departure_date.slice(0, 7);
          setSelectedMonthForCalendar(month);
        }
      })
      .catch(() => setAvailableDepartureDates([]))
      .finally(() => setAvailableDepartureDatesLoading(false));
  }, [cookieSummary?.product_id, cookieSummary?.product_slug, language]);

  /** Seçili ay için kişi başı fiyatlar (TourRadar tarzı ay bazlı fiyat göstermek için) */
  useEffect(() => {
    const productIdNum = cookieSummary?.product_id;
    const slug = cookieSummary?.product_slug;
    if (!selectedMonthForCalendar || !/^\d{4}-\d{2}$/.test(selectedMonthForCalendar)) {
      setMonthPrices([]);
      return;
    }
    if (!productIdNum && !slug) {
      setMonthPrices([]);
      return;
    }
    const adultsCount = rooms.length > 0 ? rooms.reduce((s, r) => s + (r.adults ?? 0), 0) : Number(cookieSummary?.rooms?.[0]?.adults ?? "2");
    const childrenCount = rooms.length > 0 ? rooms.reduce((s, r) => s + (r.children ?? 0), 0) : Number(cookieSummary?.rooms?.[0]?.children ?? "0");
    const params = new URLSearchParams({
      month: selectedMonthForCalendar,
      pax_count: String(Math.max(1, adultsCount)),
      child_count: String(Math.max(0, childrenCount)),
      language: language ?? "en",
    });
    if (productIdNum) params.set("product_id", String(productIdNum));
    else if (slug) params.set("slug", slug);
    setMonthPricesLoading(true);
    fetch(`/api/tour-departure-dates/prices?${params.toString()}`)
      .then((res) => res.json())
      .then((data: {
        success?: boolean;
        data?: Array<{
          departure_date?: string;
          status?: string;
          discount_percentage?: number | null;
          remaining_pax?: number | null;
          is_bookable?: boolean;
          availability?: string;
          calculated_price?: {
            success?: boolean;
            price_per_person?: number | null;
            final_price?: number;
            original_price_per_person?: number | null;
            discount_amount?: number;
            applied_campaigns?: Array<{ name?: string }>;
          };
        }>;
      }) => {
        if (!data?.success || !Array.isArray(data.data)) {
          setMonthPrices([]);
          return;
        }
        const pax = Math.max(1, adultsCount);
        const list = data.data.map((d) => {
          const calc = d.calculated_price;
          const success = calc && (calc as { success?: boolean }).success !== false;
          let pricePerPerson: number | null = success && calc?.price_per_person != null ? Number(calc.price_per_person) : null;
          if (pricePerPerson == null && success && calc?.final_price != null && Number.isFinite(calc.final_price) && pax > 0) {
            pricePerPerson = Number(calc.final_price) / pax;
          }
          return {
            departure_date: String(d.departure_date ?? "").slice(0, 10),
            price_per_person: pricePerPerson,
            status: d.status,
            discount_percentage: d.discount_percentage != null ? Number(d.discount_percentage) : null,
            original_price_per_person: success && calc?.original_price_per_person != null ? Number(calc.original_price_per_person) : null,
            discount_amount: success && calc?.discount_amount != null ? Number(calc.discount_amount) : null,
            applied_campaigns: success && Array.isArray(calc?.applied_campaigns) ? calc.applied_campaigns : undefined,
          };
        });
        setMonthPrices(list);
        setAvailableDepartureDates((prev) => {
          const byDate = new Map(prev.map((d) => [d.departure_date, d]));
          data.data?.forEach((row) => {
            const date = String(row.departure_date ?? "").slice(0, 10);
            if (!date) return;
            const existing = byDate.get(date) ?? { departure_date: date };
            byDate.set(date, {
              ...existing,
              remaining_pax: row.remaining_pax != null ? Number(row.remaining_pax) : existing.remaining_pax ?? null,
              is_bookable: row.is_bookable ?? existing.is_bookable,
              availability: row.availability ?? existing.availability,
              discount_percentage:
                row.discount_percentage != null
                  ? Number(row.discount_percentage)
                  : existing.discount_percentage ?? null,
            });
          });
          return Array.from(byDate.values()).sort((a, b) => a.departure_date.localeCompare(b.departure_date));
        });
      })
      .catch(() => setMonthPrices([]))
      .finally(() => setMonthPricesLoading(false));
  }, [selectedMonthForCalendar, cookieSummary?.product_id, cookieSummary?.product_slug, cookieSummary?.rooms, rooms, language]);

  /** Ürün metası + tour_pricing: önce tur detayından taşınan sessionStorage (fiyat ek API’siz), sonra gerekirse tek product isteği */
  useEffect(() => {
    const slug = cookieSummary?.product_slug;
    if (!slug || !language) return;
    let usedPricingFromStorage = false;
    if (
      initialTourPricing == null &&
      initialProductCurrency == null &&
      typeof window !== "undefined"
    ) {
      try {
        const raw = sessionStorage.getItem(RESERVATION_PRICING_STORAGE_KEY);
        if (raw) {
          const stored = JSON.parse(raw) as {
            product_slug?: string;
            tour_pricing?: unknown[];
            currency?: { symbol?: string; code?: string };
          };
          if (stored?.product_slug === slug && Array.isArray(stored?.tour_pricing) && stored.tour_pricing.length > 0) {
            setProductTourPricing(stored.tour_pricing as TourPricingPeriod[]);
            if (stored.currency && typeof stored.currency === "object") {
              setProductCurrency({ symbol: stored.currency.symbol, code: stored.currency.code });
            }
            usedPricingFromStorage = true;
          }
        }
      } catch {
        // ignore
      }
    }
    const productKey = `products:${slug}:${language}`;
    getCachedOrFetch(productKey, () => fetch(`/api/products/${encodeURIComponent(slug)}?language=${language}&type=tour`).then((res) => res.json()))
      .then((data) => {
        const product = data?.data ?? data;
        const metas = product?.metas;
        if (metas && typeof metas === "object") {
          setProductMetas({
            min_group_size: metas.min_group_size != null ? Number(metas.min_group_size) : undefined,
            max_group_size: metas.max_group_size != null ? Number(metas.max_group_size) : undefined,
            min_ages: metas.min_ages != null && String(metas.min_ages).trim() !== "" ? metas.min_ages : null,
            child_pax_age_threshold_private: metas.child_pax_age_threshold_private != null ? Number(metas.child_pax_age_threshold_private) : undefined,
          });
          setProductStartCity(metas.departure_city != null ? String(metas.departure_city).trim() : metas.start_location != null ? String(metas.start_location).trim() : "");
          setProductEndCity(metas.end_city != null ? String(metas.end_city).trim() : metas.end_location != null ? String(metas.end_location).trim() : "");
          const dur = metas.tour_duration;
          if (dur != null && typeof dur === "object" && !Array.isArray(dur)) {
            const d = dur as { day?: number; night?: number };
            setTourDurationDays(d.day != null ? Number(d.day) : d.night != null ? Number(d.night) : null);
          } else if (typeof dur === "string" && /^\d+/.test(dur)) {
            const n = parseInt(dur, 10);
            setTourDurationDays(Number.isFinite(n) ? n : null);
          } else {
            setTourDurationDays(null);
          }
        } else {
          setProductStartCity("");
          setProductEndCity("");
          setTourDurationDays(null);
        }
        if (!usedPricingFromStorage && initialTourPricing == null) {
          const raw = product?.tour_pricing ?? (product as { tourPricing?: unknown })?.tourPricing;
          if (Array.isArray(raw)) {
            setProductTourPricing(raw);
          }
        }
        if (!usedPricingFromStorage && initialProductCurrency == null && product?.currency && typeof product.currency === "object") {
          setProductCurrency({
            symbol: product.currency.symbol,
            code: product.currency.code,
          });
        } else if (!usedPricingFromStorage && initialProductCurrency == null) {
          setProductCurrency(null);
        }
      })
      .catch(() => {});
  }, [cookieSummary?.product_slug, language, initialTourPricing, initialProductCurrency]);

  /** Çerezdeki ürün adını sadece client'ta göster (hydration uyumu: sunucu ve ilk client aynı içeriği render eder) */
  useEffect(() => {
    setDisplayProductName(cookieSummary?.product_name || "");
  }, [cookieSummary?.product_name]);

  /** Oda/yolcu değişince participant slot'ları yeniden oluştur ve çerezi güncelle (oda kapasitesi normalize edilir) */
  const handleRoomsChange = useCallback(
    (nextRooms: Room[]) => {
      let normalized = nextRooms.map(normalizeRoom);
      const maxTravelers = effectiveMaxTravelersRef.current;
      if (maxTravelers != null && maxTravelers > 0 && totalPaxFromRooms(normalized) > maxTravelers) {
        normalized = clampRoomsToMaxTravelers(normalized, maxTravelers);
      }
      setRooms(normalized);
      setParticipantsByRoom(
        normalized.map((room) => {
          const slots: ParticipantField[] = [];
          const adults = room.adults ?? 0;
          const children = room.children ?? 0;
          for (let i = 0; i < adults; i++) {
            slots.push({ type: "adult", first_name: "", last_name: "", national_id: "", passport_no: "", nationality: "", save_for_future: false });
          }
          for (let i = 0; i < children; i++) {
            slots.push({ type: "child", first_name: "", last_name: "", national_id: "", passport_no: "", nationality: "", birth_date: "", save_for_future: false });
          }
          return slots;
        })
      );
      if (typeof document !== "undefined" && cookieSummary) {
        const maxAge = RESERVATION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
        const nextCookie: CookieSummary = {
          ...cookieSummary,
          rooms: normalized.map((r) => ({ adults: r.adults, children: r.children, childAges: r.childAges })),
          guest_count: normalized.reduce((s, r) => s + (r.adults ?? 0) + (r.children ?? 0), 0),
          child_count: normalized.reduce((s, r) => s + (r.children ?? 0), 0),
        };
        document.cookie = `${RESERVATION_CHECKOUT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(nextCookie))}; path=/; max-age=${maxAge}; SameSite=Lax`;
      }
    },
    [cookieSummary]
  );

  /** Book Now sayfasında çıkış tarihi seçildiğinde state + çerez güncelle */
  const handleDepartureDateChange = useCallback(
    (dateValue: string) => {
      if (dateValue === "-" || !dateValue.trim()) return;
      setSelectedDepartureDate(dateValue);
      if (typeof document !== "undefined" && cookieSummary) {
        const maxAge = RESERVATION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
        const nextCookie: CookieSummary = {
          ...cookieSummary,
          travel_date: dateValue,
          check_in_date: dateValue,
        };
        document.cookie = `${RESERVATION_CHECKOUT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(nextCookie))}; path=/; max-age=${maxAge}; SameSite=Lax`;
      }
    },
    [cookieSummary]
  );

  const t = (key: string, fallback = "") => translations[key] || getVariable(key, fallback);

  /** Etkin çıkış tarihi: kullanıcı seçtiyse o, yoksa çerezdeki (fiyat ve gösterim için) */
  const effectiveDepartureDate = selectedDepartureDate ?? cookieSummary?.travel_date ?? cookieSummary?.check_in_date ?? "-";
  const productSlug = cookieSummary?.product_slug || "";
  const productId = Number(cookieSummary?.product_id || "0");
  const adults = rooms.length > 0 ? rooms.reduce((s, r) => s + (r.adults ?? 0), 0) : Number(cookieSummary?.rooms?.[0]?.adults ?? "2");
  const children = rooms.length > 0 ? rooms.reduce((s, r) => s + (r.children ?? 0), 0) : Number(cookieSummary?.rooms?.[0]?.children ?? "0");
  const periodId = cookieSummary?.period_id != null ? String(cookieSummary.period_id) : "-";
  const cookieTotalPrice = Number(cookieSummary?.total_price || 0);
  const perPersonPrice = adults > 0 && cookieTotalPrice > 0 ? cookieTotalPrice / adults : 0;
  const currencySymbol = cookieSummary?.currency?.symbol || "€";
  const productName = cookieSummary?.product_name || "";

  const backToDatesHref = useMemo(() => {
    const datesHash = "#dates-and-availability";
    const returnPath = cookieSummary?.return_path?.trim();
    if (returnPath && returnPath.startsWith("/")) {
      return returnPath.includes("#") ? returnPath : `${returnPath}${datesHash}`;
    }
    if (productSlug && pageUrls) {
      const detail = buildProductDetailUrl(
        cookieSummary?.product_type || "tour",
        productSlug,
        pageUrls,
        language,
        defaultLanguage,
      );
      if (detail && detail !== "#") {
        return `${detail}${datesHash}`;
      }
    }
    return language === defaultLanguage ? "/" : `/${language}`;
  }, [
    cookieSummary?.return_path,
    cookieSummary?.product_type,
    productSlug,
    pageUrls,
    language,
    defaultLanguage,
  ]);
  const hasValidSelection = Boolean(productId > 0 && effectiveDepartureDate !== "-");
  const productType = cookieSummary?.product_type || "tour";
  const hasPrice = !Number.isNaN(perPersonPrice) && perPersonPrice > 0;

  /** API hata cevabından müşteriye gösterilecek metni çıkarır; asla ham JSON göstermez, site diline göre çeviri anahtarı kullanır. */
  const getReservationErrorMessage = useCallback(
    (data: unknown, fallbackKey: string, fallbackEn: string): string => {
      let msg = "";
      if (data != null && typeof data === "object" && !Array.isArray(data)) {
        const o = data as Record<string, unknown>;
        msg = (typeof o.message === "string" ? o.message : typeof o.error === "string" ? o.error : "") || "";
        if (!msg && o.errors && typeof o.errors === "object") {
          const first = Object.values(o.errors as Record<string, unknown>)[0];
          msg = Array.isArray(first) ? (first[0] as string) : typeof first === "string" ? first : "";
        }
      } else if (typeof data === "string" && !data.trimStart().startsWith("{")) {
        msg = data.trim();
      }
      if (!msg || msg.trimStart().startsWith("{")) {
        return t(fallbackKey, fallbackEn);
      }
      const m = msg.toLowerCase();
      if (
        m.includes("fiyat hesaplanamadı") ||
        m.includes("price could not be calculated") ||
        m.includes("could not calculate") ||
        m.includes("price_calculation_failed")
      ) {
        return t(
          "reservation.error_price_could_not_be_calculated",
          "Price could not be calculated. Please check date and guest details and try again."
        );
      }
      if (
        m.includes("robot doğrulama") ||
        m.includes("captcha") ||
        m.includes("recaptcha") ||
        m.includes("captcha.error")
      ) {
        return t("common.captcha_required", "Please complete the robot verification.");
      }
      return msg;
    },
    [t, language]
  );

  // Rezervasyon formu sözleşmeleri: forms/details – cache ile tekrar istek önlenir
  useEffect(() => {
    if (productId <= 0 || !productType) {
      setAgreements([]);
      return;
    }
    const params = new URLSearchParams({
      product_id: String(productId),
      product_type: productType,
      for_reservation: '1',
    });
    const key = `forms:details:reservation:${productId}:${productType}`;
    getCachedOrFetch(key, () => fetch(`/api/forms/details?${params.toString()}`).then((res) => res.json()))
      .then((data: {
        success?: boolean;
        data?: {
          language?: { id?: number };
          agreements?: { required?: Agreement[]; optional?: Agreement[] };
        };
      }) => {
        if (data?.data?.language?.id) {
          setReservationFormLanguageId(data.data.language.id);
        }
        if (data?.success && data?.data?.agreements) {
          const required = (data.data.agreements.required || []).map((a) => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            is_required: true,
            content: a.content,
          }));
          const optional = (data.data.agreements.optional || []).map((a) => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            is_required: false,
            content: a.content,
          }));
          setAgreements([...required, ...optional]);
        } else {
          setAgreements([]);
        }
      })
      .catch(() => setAgreements([]));
  }, [productId, productType]);

  // Zorunlu sözleşmeler varsayılan kabul, opsiyoneller işaretsiz
  useEffect(() => {
    const initialState: Record<number, boolean> = {};
    agreements.forEach((agreement) => {
      initialState[agreement.id] = agreement.is_required === true;
    });
    setAgreementsState(initialState);
  }, [agreements]);

  // Oda bazlı fiyat hesaplama: her oda için /api/price/calculate (per person, single, extra bed, child ages)
  const effectiveRoomsForPrice = useMemo(() => {
    if (rooms.length > 0) return rooms;
    const fromCookie = cookieSummary?.rooms ?? [{ adults: 2, children: 0, childAges: [] }];
    return fromCookie.map((r) => normalizeRoom({
      adults: r.adults ?? 2,
      children: r.children ?? 0,
      childAges: Array.isArray(r.childAges) ? r.childAges : [],
    }));
  }, [rooms, cookieSummary?.rooms]);
  /** Fiyat: çıkış tarihinin dönemi + odalar. Kampanya indirimi varsa monthPrices'taki kişi başı fiyat kullanılır. */
  const priceData = useMemo(() => {
    const periods = normalizeTourPricing(productTourPricing ?? []);
    if (!periods.length || effectiveDepartureDate === "-" || effectiveRoomsForPrice.length === 0) return null;
    const normalizedDate = normalizeDepartureDate(effectiveDepartureDate);
    if (normalizedDate === "-") return null;

    const baseResult = calculateGroupPriceFromTourPricing(
      periods,
      normalizedDate,
      effectiveRoomsForPrice,
      productCurrency ?? undefined
    );
    if (!baseResult) return null;

    const monthPriceRow = monthPrices.find((p) => (p.departure_date ?? "").slice(0, 10) === normalizedDate);
    const discountedPerPerson =
      monthPriceRow?.price_per_person != null && Number.isFinite(Number(monthPriceRow.price_per_person))
        ? Number(monthPriceRow.price_per_person)
        : null;

    if (discountedPerPerson != null && baseResult.periodUnitPrices.perPerson > 0) {
      return applyDiscountedPerPersonToGroupResult(baseResult, discountedPerPerson);
    }
    return baseResult;
  }, [productTourPricing, effectiveDepartureDate, effectiveRoomsForPrice, productCurrency, monthPrices]);

  /** Tarih seçilmediğinde veya eşleşmediğinde gösterilecek "From" minimum fiyat (tour_pricing dönemlerinden en düşük toplam) */
  const minFromPrice = useMemo(() => {
    const periods = normalizeTourPricing(productTourPricing ?? []);
    if (!periods.length || effectiveRoomsForPrice.length === 0) return 0;
    let minTotal = 0;
    for (const period of periods) {
      const rows = period?.rows ?? [];
      if (rows.length === 0) continue;
      const row = rows[0];
      const perPerson = row.perPersonDouble ?? 0;
      const singleRoom = row.singleRoom ?? null;
      const extraBed = row.extraBed ?? 0;
      const childRules = row.childRules ?? [];
      const fallbackChildFee = row.childrenFee ?? 0;
      let total = 0;
      for (const room of effectiveRoomsForPrice) {
        const a = room.adults ?? 0;
        const childAges = room.childAges ?? [];
        if (a === 1 && singleRoom != null) total += singleRoom;
        else if (a === 2) total += perPerson * 2;
        else if (a >= 3) total += perPerson * 2 + (a - 2) * extraBed;
        else if (a > 0) total += perPerson * a;
        for (const age of childAges) {
          const rule = childRules.find((r) => age >= r.min_age && age <= r.max_age);
          total += rule ? rule.fee : fallbackChildFee;
        }
      }
      if (total > 0 && (minTotal === 0 || total < minTotal)) minTotal = total;
    }
    return minTotal;
  }, [productTourPricing, effectiveRoomsForPrice]);

  const totalTravelers = adults + children;
  const minGroupSize = productMetas?.min_group_size != null ? Number(productMetas.min_group_size) : null;
  const maxGroupSize = productMetas?.max_group_size != null ? Number(productMetas.max_group_size) : null;

  const normalizedEffectiveDeparture = effectiveDepartureDate !== "-" ? normalizeDepartureDate(effectiveDepartureDate) : null;

  const selectedDepartureAvailability = useMemo(() => {
    if (!normalizedEffectiveDeparture) return null;
    return availableDepartureDates.find((d) => d.departure_date === normalizedEffectiveDeparture) ?? null;
  }, [availableDepartureDates, normalizedEffectiveDeparture]);

  const departureRemainingPax =
    selectedDepartureAvailability?.remaining_pax != null
      ? Number(selectedDepartureAvailability.remaining_pax)
      : null;

  const isGroupTourBooking = useMemo(
    () =>
      (productTourPricing ?? []).some((p) => (p.periodType ?? "") === "group") ||
      availableDepartureDates.length > 0,
    [productTourPricing, availableDepartureDates.length],
  );

  const effectiveMaxTravelers = useMemo(() => {
    let cap: number | null = maxGroupSize != null && !Number.isNaN(maxGroupSize) ? maxGroupSize : null;
    if (isGroupTourBooking && departureRemainingPax != null && !Number.isNaN(departureRemainingPax)) {
      cap = cap == null ? departureRemainingPax : Math.min(cap, departureRemainingPax);
    }
    return cap;
  }, [maxGroupSize, isGroupTourBooking, departureRemainingPax]);

  effectiveMaxTravelersRef.current = effectiveMaxTravelers;

  const meetsDepartureQuota =
    effectiveMaxTravelers == null || totalTravelers <= effectiveMaxTravelers;

  const selectedDepartureBookable =
    selectedDepartureAvailability?.is_bookable !== false &&
    selectedDepartureAvailability?.availability !== "sold_out" &&
    selectedDepartureAvailability?.availability !== "stop_sale" &&
    selectedDepartureAvailability?.status !== "stop_sale";

  const canAddAnotherRoom = useMemo(() => {
    const roomCount = rooms.length > 0 ? rooms.length : 1;
    if (roomCount >= 10) return false;
    if (effectiveMaxTravelers != null && totalTravelers >= effectiveMaxTravelers) return false;
    if (effectiveMaxTravelers != null && totalTravelers + 1 > effectiveMaxTravelers) return false;
    return true;
  }, [rooms.length, effectiveMaxTravelers, totalTravelers]);

  useEffect(() => {
    if (effectiveMaxTravelers == null || rooms.length === 0) return;
    if (totalPaxFromRooms(rooms) <= effectiveMaxTravelers) return;
    handleRoomsChange(clampRoomsToMaxTravelers(rooms, effectiveMaxTravelers));
  }, [effectiveMaxTravelers, effectiveDepartureDate, rooms, handleRoomsChange]);
  const adultsSubtotal = hasPrice ? adults * perPersonPrice : 0;
  /** tour_pricing yoksa seçili tarih için /prices API'den gelen kişi başı fiyat ile basit toplam (yedek) */
  const fallbackTotalFromMonthPrices = useMemo(() => {
    if (priceData != null || effectiveDepartureDate === "-") return 0;
    const normalizedDate = normalizeDepartureDate(effectiveDepartureDate);
    if (normalizedDate === "-") return 0;
    const totalAdults = effectiveRoomsForPrice.reduce((s, r) => s + (r.adults ?? 0), 0);
    if (totalAdults <= 0) return 0;
    const row = monthPrices.find((p) => (p.departure_date ?? "").slice(0, 10) === normalizedDate);
    const perPerson = row?.price_per_person != null ? Number(row.price_per_person) : null;
    if (perPerson == null || !Number.isFinite(perPerson)) return 0;
    return perPerson * totalAdults;
  }, [priceData, effectiveDepartureDate, effectiveRoomsForPrice, monthPrices]);

  const estimateTotal = priceData != null ? priceData.total_price : fallbackTotalFromMonthPrices;

  const checkoutPayAmount = useMemo(() => {
    if (partialPaymentActive && partialPaymentBounds && payNowAmount.trim() !== "") {
      const n = parseFloat(payNowAmount);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return estimateTotal > 0 ? estimateTotal : 0;
  }, [partialPaymentActive, partialPaymentBounds, payNowAmount, estimateTotal]);

  const paymentModeForSubmit =
    partialPaymentActive && partialPaymentBounds && checkoutPayAmount + 0.009 < (partialPaymentBounds.max_amount ?? estimateTotal)
      ? "partial"
      : "full";

  const balanceAfterPayNow = useMemo(() => {
    if (paymentModeForSubmit !== "partial" || estimateTotal <= 0) return 0;
    return Math.max(0, Math.round((estimateTotal - checkoutPayAmount) * 100) / 100);
  }, [paymentModeForSubmit, estimateTotal, checkoutPayAmount]);

  const partialScheduleFees = useMemo(() => {
    if (!partialPaymentActive || !partialPaymentPolicy || paymentSchedule.length === 0) {
      return null;
    }
    return computeScheduleLateFees(partialPaymentPolicy, paymentSchedule);
  }, [partialPaymentActive, partialPaymentPolicy, paymentSchedule]);

  const grandTotalWithLateFees = useMemo(() => {
    if (!partialScheduleFees || partialScheduleFees.feesTotal <= 0) {
      return estimateTotal;
    }
    return computeGrandTotalWithLateFees(estimateTotal, partialScheduleFees.feesTotal);
  }, [estimateTotal, partialScheduleFees]);

  const cardPayableAmount = cardCommissionNow?.total_with_commission ?? null;

  const cardCommissionNowAmount = cardCommissionNow?.commission_amount ?? 0;

  const productTotalBeforeCard = useMemo(() => {
    if (partialPaymentActive && paymentModeForSubmit === "partial" && partialScheduleFees) {
      return grandTotalWithLateFees;
    }
    return estimateTotal;
  }, [
    partialPaymentActive,
    paymentModeForSubmit,
    partialScheduleFees,
    grandTotalWithLateFees,
    estimateTotal,
  ]);

  const grandTotalWithCard = useMemo(() => {
    if (paymentMethod !== "card") return productTotalBeforeCard;
    if (cardCommissionNowAmount <= 0.009) return productTotalBeforeCard;
    return Math.round((productTotalBeforeCard + cardCommissionNowAmount) * 100) / 100;
  }, [paymentMethod, productTotalBeforeCard, cardCommissionNowAmount]);
  const displayCurrencySymbol = priceData?.currency?.symbol ?? productCurrency?.symbol ?? currencySymbol;
  const perPersonDisplay = adults > 0 && estimateTotal > 0 ? estimateTotal / adults : perPersonPrice;
  const hasPriceFromApi = priceData != null && priceData.total_price > 0;
  const showFromPrice = !hasPriceFromApi && minFromPrice > 0;
  /** Seçili tarih için kampanya adı (Book Now özetinde göstermek üzere) */
  const campaignNameForSelectedDate = useMemo(() => {
    if (effectiveDepartureDate === "-") return null;
    const normalized = normalizeDepartureDate(effectiveDepartureDate);
    const row = monthPrices.find((p) => (p.departure_date ?? "").slice(0, 10) === normalized);
    const name = row?.applied_campaigns?.[0]?.name;
    return name && row?.original_price_per_person != null && row?.price_per_person != null && row.original_price_per_person > row.price_per_person ? name : null;
  }, [effectiveDepartureDate, monthPrices]);
  const locale = language === "tr" ? "tr-TR" : "en-GB";
  const meetsMinGroup = minGroupSize == null || totalTravelers >= minGroupSize;
  const meetsMaxGroup = maxGroupSize == null || totalTravelers <= maxGroupSize;
  const formatMoney = (amount: number) =>
    `${displayCurrencySymbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    if (!productId || !(estimateTotal > 0)) {
      setPartialPaymentOffered(false);
      setWantsPartialPayment(false);
      setPartialPaymentBounds(null);
      setPartialPaymentPolicy(null);
      setPaymentSchedule([]);
      setCardStorageAvailable(false);
      setSaveCardForFuture(false);
      return;
    }
    let cancelled = false;
    fetchPartialPaymentPolicy(productId, estimateTotal).then((data) => {
      if (cancelled) return;
      const enabled = !!data?.policy?.enabled && !!data.bounds;
      setPartialPaymentOffered(enabled);
      if (!enabled) {
        setWantsPartialPayment(false);
      }
      setPartialPaymentBounds(enabled && data.bounds ? data.bounds : null);
      setPartialPaymentPolicy(enabled && data.policy ? data.policy : null);
      const storageOn = !!data?.card_storage?.enabled;
      setCardStorageAvailable(storageOn);
      if (!storageOn) {
        setSaveCardForFuture(false);
      }
      if (!enabled) {
        setPayNowAmount("");
        setPaymentSchedule([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [productId, estimateTotal]);

  useEffect(() => {
    if (!wantsPartialPayment || !partialPaymentBounds || !(estimateTotal > 0)) return;
    setPayNowAmount((prev) => {
      if (!prev) return String(partialPaymentBounds.min_amount);
      return prev;
    });
  }, [wantsPartialPayment, estimateTotal, partialPaymentBounds?.min_amount, partialPaymentBounds?.max_amount]);

  const handleWantsPartialPaymentChange = useCallback(
    (checked: boolean) => {
      setWantsPartialPayment(checked);
      if (!checked) {
        setPaymentSchedule([]);
        setPayNowAmount("");
        if (estimateTotal > 0) {
          setTransferAmount(String(estimateTotal));
        }
        return;
      }
      setPaymentSchedule([]);
      if (partialPaymentBounds) {
        const minStr = String(partialPaymentBounds.min_amount);
        setPayNowAmount(minStr);
        if (paymentMethod === "bank") setTransferAmount(minStr);
      }
    },
    [estimateTotal, partialPaymentBounds, paymentMethod],
  );

  /** Banka havalesi seçildiğinde form alanlarını rezervasyon formu ve tahmini tutarla doldur (InvoicePayment ile aynı mantık) */
  useEffect(() => {
    if (paymentMethod !== "bank") return;
    const name = [form.first_name?.trim(), form.last_name?.trim()].filter(Boolean).join(" ");
    if (name) setSenderName((prev) => prev || name);
    if (form.email?.trim()) setSenderEmail((prev) => prev || form.email.trim());
    if (form.phone?.trim()) setSenderPhone((prev) => prev || form.phone.trim());
    const defaultAmt = checkoutPayAmount > 0 ? checkoutPayAmount : estimateTotal;
    if (defaultAmt > 0) setTransferAmount((prev) => prev || String(defaultAmt));
    const today = new Date().toISOString().split("T")[0];
    setTransferDate((prev) => prev || today);
  }, [paymentMethod, form.first_name, form.last_name, form.email, form.phone, estimateTotal, checkoutPayAmount]);

  /** Kart seçiliyken yalnızca şimdi ödenecek tutar için komisyon */
  useEffect(() => {
    if (paymentMethod !== "card" || !(checkoutPayAmount > 0)) {
      setCardCommissionNow(null);
      return;
    }
    let cancelled = false;
    fetchCardCommissionQuote(checkoutPayAmount).then((quote) => {
      if (!cancelled) setCardCommissionNow(quote);
    });
    return () => {
      cancelled = true;
    };
  }, [paymentMethod, checkoutPayAmount]);

  useEffect(() => {
    if (paymentMethod !== "card" || !(checkoutPayAmount > 0)) {
      setInstallments([]);
      setInstallment(1);
      return;
    }
    const digits = cardNumber.replace(/\s/g, "");
    if (digits.length < 6) {
      setInstallments([]);
      setInstallment(1);
      return;
    }
    const bin = digits.slice(0, 6);
    let cancelled = false;
    paymentService
      .getInstallmentsByAmount(bin, checkoutPayAmount)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data.installments) && data.installments.length > 0 ? data.installments : [];
        setInstallments(list);
        setInstallment(1);
      })
      .catch(() => {
        if (!cancelled) {
          setInstallments([]);
          setInstallment(1);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [paymentMethod, checkoutPayAmount, cardNumber]);

  const formattedDepartureDate =
    effectiveDepartureDate !== "-"
      ? new Date(effectiveDepartureDate).toLocaleDateString(locale, {
          weekday: "long",
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "-";

  /** Ay bazlı takvim: mevcut tarihlerden benzersiz aylar (Y-m), sıralı */
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    availableDepartureDates.forEach((d) => set.add(d.departure_date.slice(0, 7)));
    return Array.from(set).sort();
  }, [availableDepartureDates]);

  /** Seçili aydaki tarihler (fiyat ve indirim bilgisiyle). Fiyat: önce API monthPrices, yoksa sayfa yüklenirken gelen tour_pricing ile hesaplanır. */
  const datesInSelectedMonth = useMemo(() => {
    if (!selectedMonthForCalendar) return [];
    const periods = normalizeTourPricing(productTourPricing ?? []);
    const totalAdults = effectiveRoomsForPrice.reduce((s, r) => s + (r.adults ?? 0), 0);
    const inMonth = availableDepartureDates.filter((d) => d.departure_date.startsWith(selectedMonthForCalendar));
    return inMonth.map((d) => {
      const priceRow = monthPrices.find((p) => p.departure_date === d.departure_date);
      let pricePerPerson: number | null = priceRow?.price_per_person ?? null;
      if (pricePerPerson == null && periods.length > 0 && totalAdults > 0) {
        const computed = calculateGroupPriceFromTourPricing(
          periods,
          d.departure_date,
          effectiveRoomsForPrice,
          productCurrency ?? undefined
        );
        if (computed?.total_price != null && Number.isFinite(computed.total_price)) {
          pricePerPerson = computed.total_price / totalAdults;
        }
      }
      const hasCampaignDiscount =
        priceRow?.applied_campaigns?.length &&
        priceRow.original_price_per_person != null &&
        priceRow.price_per_person != null &&
        priceRow.original_price_per_person > priceRow.price_per_person;
      const appliedCampaignName = hasCampaignDiscount ? priceRow?.applied_campaigns?.[0]?.name ?? null : null;
      const isDiscounted =
        hasCampaignDiscount ||
        d.status === "discounted" ||
        (d.discount_percentage != null && Number(d.discount_percentage) > 0) ||
        (priceRow?.status === "discounted") ||
        (priceRow?.discount_percentage != null && Number(priceRow.discount_percentage) > 0);
      return {
        departure_date: d.departure_date,
        status: d.status,
        price_per_person: pricePerPerson,
        original_price_per_person: priceRow?.original_price_per_person ?? null,
        appliedCampaignName: appliedCampaignName ?? undefined,
        isDiscounted,
      };
    });
  }, [availableDepartureDates, selectedMonthForCalendar, monthPrices, productTourPricing, effectiveRoomsForPrice, productCurrency]);

  const buildParticipantsPayload = useCallback(() => {
    const list: Array<{
      room_index: number;
      guest_index: number;
      type: "adult" | "child";
      first_name: string;
      last_name: string;
      tc_no?: string;
      passport_no?: string;
      nationality?: string;
      birth_date?: string | null;
      save_for_future?: boolean;
    }> = [];
    participantsByRoom.forEach((travelers, roomIndex) => {
      travelers.forEach((p, guestIndex) => {
        list.push({
          room_index: roomIndex,
          guest_index: guestIndex,
          type: p.type,
          first_name: p.first_name,
          last_name: p.last_name,
          tc_no: p.national_id || undefined,
          passport_no: p.passport_no || undefined,
          nationality: p.nationality || undefined,
          birth_date: p.type === "child" ? (p.birth_date?.trim() || null) : undefined,
          save_for_future: p.save_for_future || undefined,
        });
      });
    });
    return list;
  }, [participantsByRoom]);

  const requiredAgreements = useMemo(() => agreements.filter((a) => a.is_required), [agreements]);
  const optionalAgreements = useMemo(() => agreements.filter((a) => !a.is_required), [agreements]);

  const handleAgreementChange = useCallback((agreementId: number, checked: boolean) => {
    setAgreementsState((prev) => ({ ...prev, [agreementId]: checked }));
  }, []);
  const handleViewAgreement = useCallback((agreement: Agreement) => {
    setSelectedAgreement(agreement);
    setIsAgreementModalOpen(true);
  }, []);
  const handleAgreementAccept = useCallback((agreementId: number) => {
    setAgreementsState((prev) => ({ ...prev, [agreementId]: true }));
  }, []);

  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const participantFieldKey = (roomIndex: number, guestIndex: number, field: string) =>
    `p_${roomIndex}_${guestIndex}_${field}`;

  const validateForm = useCallback((): boolean => {
    const errors: BookNowFieldErrors = {};
    const fn = form.first_name?.trim() || "";
    const ln = form.last_name?.trim() || "";
    const em = form.email?.trim() || "";
    if (!fn) errors.first_name = t("reservation.validation.first_name", "First name is required");
    if (!ln) errors.last_name = t("reservation.validation.last_name", "Last name is required");
    if (!em) {
      errors.email = t("reservation.validation.email", "Email is required");
    } else if (!EMAIL_REGEX.test(em)) {
      errors.email = t("payment.error.invalid_email", "Enter a valid email address");
    }
    const phone = form.phone?.trim() || "";
    if (phone && !/^[\d\s\-+()]+$/.test(phone)) {
      errors.phone = t("payment.error.invalid_phone", "Enter a valid phone number");
    }

    const billAddr = billing.address.trim();
    const billCity = billing.city.trim();
    const billCountry = billing.country.trim();
    if (!billCountry) errors.billing_country = t("reservation.validation.country", "Country is required");
    if (!billCity) errors.billing_city = t("reservation.validation.city", "City is required");
    if (!billAddr || billAddr.length < 3) {
      errors.billing_address = t("reservation.validation.address", "Enter a valid billing address");
    }

    if (billingContactDifferent) {
      if (!billingContact.first_name.trim()) {
        errors.billing_first_name = t("reservation.validation.first_name", "First name is required");
      }
      if (!billingContact.last_name.trim()) {
        errors.billing_last_name = t("reservation.validation.last_name", "Last name is required");
      }
      const billEm = billingContact.email.trim();
      if (!billEm) {
        errors.billing_email = t("reservation.validation.email", "Email is required");
      } else if (!EMAIL_REGEX.test(billEm)) {
        errors.billing_email = t("payment.error.invalid_email", "Enter a valid email address");
      }
    }

    participantsByRoom.forEach((travelers, roomIndex) => {
      travelers.forEach((p, guestIndex) => {
        const key = (field: string) => participantFieldKey(roomIndex, guestIndex, field);
        if (!p.first_name?.trim()) {
          errors[key("first_name")] = t(
            "reservation.participant_name_required",
            language === "tr" ? "Tüm yolcular için ad ve soyad zorunludur." : "First and last name are required for every traveler.",
          );
        }
        if (!p.last_name?.trim()) {
          errors[key("last_name")] = t(
            "reservation.participant_name_required",
            language === "tr" ? "Tüm yolcular için ad ve soyad zorunludur." : "First and last name are required for every traveler.",
          );
        }
        if (!p.nationality?.trim()) {
          errors[key("nationality")] = t(
            "reservation.participant_nationality_required",
            language === "tr" ? "Tüm yolcular için uyruk seçilmelidir." : "Nationality is required for every traveler.",
          );
        }
        if (showTc(p.nationality)) {
          if (!p.national_id?.trim() || p.national_id.length !== 11) {
            errors[key("national_id")] = t(
              "reservation.participant_tc_required",
              language === "tr" ? "Türk vatandaşları için 11 haneli T.C. kimlik no zorunludur." : "A valid 11-digit national ID is required for Turkish nationals.",
            );
          }
        } else if (p.nationality?.trim() && !p.passport_no?.trim()) {
          errors[key("passport_no")] = t(
            "reservation.participant_passport_required",
            language === "tr" ? "Yabancı uyruklu yolcular için pasaport numarası zorunludur." : "Passport number is required for non-Turkish nationals.",
          );
        }
        if (p.type === "child" && !p.birth_date?.trim()) {
          errors[key("birth_date")] = t(
            "reservation.participant_birthdate_required",
            language === "tr" ? "Çocuk yolcular için doğum tarihi zorunludur." : "Birth date is required for child travelers.",
          );
        }
      });
    });

    const allRequiredAccepted = requiredAgreements.every((a) => agreementsState[a.id] === true);
    if (requiredAgreements.length > 0 && !allRequiredAccepted) {
      setFieldErrors(errors);
      setError(getVariable("reservation.agreements_required", "Please accept all required agreements."));
      return false;
    }
    if (minGroupSize != null && totalTravelers < minGroupSize) {
      setFieldErrors(errors);
      setError(
        getVariable("reservation.min_pax_warning", "Minimum :min person booking required.").replace(/:min/g, String(minGroupSize))
      );
      return false;
    }
    if (maxGroupSize != null && totalTravelers > maxGroupSize) {
      setFieldErrors(errors);
      setError(
        getVariable("reservation.max_group_warning", "Maximum :max travelers allowed.").replace(/:max/g, String(maxGroupSize))
      );
      return false;
    }
    if (isGroupTourBooking && !selectedDepartureBookable) {
      setFieldErrors(errors);
      setError(
        getVariable(
          "reservation.departure_not_bookable",
          "This departure is no longer available. Please choose another date.",
        ),
      );
      return false;
    }
    if (isGroupTourBooking && effectiveMaxTravelers != null && totalTravelers > effectiveMaxTravelers) {
      setFieldErrors(errors);
      setError(
        getVariable(
          "reservation.departure_quota_exceeded",
          "Not enough seats available for this departure. Reduce the number of guests or choose another date.",
        ),
      );
      return false;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstKey = Object.keys(errors)[0];
      setError(errors[firstKey] ?? getVariable("reservation.fill_required_fields", "Please fix the highlighted fields."));
      if (firstKey.startsWith("p_") && participantsSectionRef.current) {
        participantsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (firstKey.startsWith("billing_") && billingSectionRef.current) {
        billingSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (firstKey.startsWith("card_") && paymentSectionRef.current) {
        paymentSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (contactSectionRef.current) {
        contactSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return false;
    }

    setFieldErrors({});
    setError(null);
    return true;
  }, [
    form.first_name,
    form.last_name,
    form.email,
    form.phone,
    billing.address,
    billing.city,
    billing.country,
    billingContactDifferent,
    billingContact.first_name,
    billingContact.last_name,
    billingContact.email,
    requiredAgreements,
    agreementsState,
    minGroupSize,
    maxGroupSize,
    totalTravelers,
    getVariable,
    isGroupTourBooking,
    selectedDepartureBookable,
    effectiveMaxTravelers,
    participantsByRoom,
    language,
    t,
  ]);

  const extractTokenFromRedirectUrl = (url: string): string | null => {
    try {
      const path = url.startsWith("http") ? new URL(url).pathname : url;
      const segments = path.split("/").filter(Boolean);
      const last = segments[segments.length - 1];
      if (last && last.length > 20) return last;
      return null;
    } catch {
      return null;
    }
  };

  /** InvoicePayment ile aynı: banka havalesi formu validasyonu (ödeme bu ekranda alınacak) */
  const validateBankTransferForm = useCallback((): string | null => {
    if (!selectedBank) return t("payment.error.select_bank", "Please select the bank for transfer");
    const amount = parseFloat(transferAmount);
    if (!transferAmount || Number.isNaN(amount) || amount <= 0) return t("payment.error.invalid_amount", "Enter a valid amount");
    const maxAmount = partialPaymentBounds?.max_amount ?? (estimateTotal || 0);
    const minAmount = partialPaymentActive && partialPaymentBounds ? partialPaymentBounds.min_amount : 0;
    if (maxAmount > 0 && amount > maxAmount) {
      const sym = productCurrency?.symbol ?? productCurrency?.code ?? "₺";
      return t("payment.error.amount_exceeds", `Amount cannot exceed ${maxAmount.toFixed(2)} ${sym}`);
    }
    if (minAmount > 0 && amount + 0.001 < minAmount) {
      return t("payment.error.amount_below_min", `Minimum amount is ${minAmount.toFixed(2)}`);
    }
    if (!transferDate) return t("payment.error.transfer_date_required", "Enter the transfer date");
    const today = new Date().toISOString().split("T")[0];
    if (transferDate > today) return t("payment.error.invalid_date", "Transfer date cannot be in the future");
    if (!senderName?.trim()) return t("payment.error.sender_name_required", "Enter the sender name");
    if (!senderEmail?.trim()) return t("payment.error.email_required", "Enter the email address");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail)) return t("payment.error.invalid_email", "Enter a valid email address");
    if (senderPhone && !/^[\d\s\-+()]+$/.test(senderPhone)) return t("payment.error.invalid_phone", "Enter a valid phone number");
    if (receiptFile) {
      const maxSize = 5 * 1024 * 1024;
      if (receiptFile.size > maxSize) return t("payment.error.file_too_large", "File size must be at most 5MB");
      const allowed = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowed.includes(receiptFile.type)) return t("payment.error.invalid_file_type", "Only JPG, PNG or PDF files are allowed");
    }
    return null;
  }, [selectedBank, transferAmount, transferDate, senderName, senderEmail, senderPhone, receiptFile, estimateTotal, productCurrency, t, partialPaymentActive, partialPaymentBounds]);

  const validatePayNowAmount = useCallback((): string | null => {
    if (!partialPaymentActive || !partialPaymentBounds) return null;
    const n = parseFloat(payNowAmount);
    if (!payNowAmount || Number.isNaN(n) || n <= 0) {
      return t("payment.error.invalid_amount", "Enter a valid amount");
    }
    if (n + 0.001 < partialPaymentBounds.min_amount) {
      return t("payment.error.amount_below_min", `Minimum amount is ${partialPaymentBounds.min_amount.toFixed(2)}`);
    }
    if (n > partialPaymentBounds.max_amount + 0.001) {
      return t("payment.error.amount_exceeds", "Amount exceeds the maximum allowed");
    }
    return null;
  }, [partialPaymentActive, partialPaymentBounds, payNowAmount, t]);

  const validateCardFields = useCallback((): BookNowFieldErrors => {
    const cardErrors: BookNowFieldErrors = {};
    if (!cardHolder.trim()) {
      cardErrors.card_holder = t("payment.error.card_holder", "Enter the name on the card");
    }
    const digits = cardNumber.replace(/\s/g, "");
    if (!digits || digits.length < 13) {
      cardErrors.card_number = t("payment.error.card_number", "Enter a valid card number");
    }
    if (!expireMonth || !expireYear) {
      cardErrors.card_expiry = t("payment.error.card_expiry", "Enter expiry date");
    }
    if (!cvc || cvc.length < 3) {
      cardErrors.card_cvc = t("payment.error.card_cvc", "Enter CVC");
    }
    return cardErrors;
  }, [cardHolder, cardNumber, expireMonth, expireYear, cvc, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!hasValidSelection || !cookieSummary) return;
      if (!validateForm()) return;

      if (paymentMethod === "card") {
        const cardErrors = validateCardFields();
        if (Object.keys(cardErrors).length > 0) {
          setPaymentMethod("card");
          setFieldErrors((prev) => ({ ...prev, ...cardErrors }));
          setError(
            t(
              "payment.error.card_incomplete",
              language === "tr"
                ? "Kart sekmesinde tüm alanları doldurun."
                : "Complete all card fields on the card tab.",
            ),
          );
          paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
      if (paymentMethod === "bank") {
        const bankErr = validateBankTransferForm();
        if (bankErr) {
          setPaymentMethod("bank");
          setError(bankErr);
          paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
      const payAmountErr = validatePayNowAmount();
      if (payAmountErr) {
        setError(payAmountErr);
        return;
      }
      if (paymentModeForSubmit === "partial" && balanceAfterPayNow > 0.009 && partialPaymentPolicy) {
        const scheduleErr = validatePaymentScheduleRows(
          balanceAfterPayNow,
          paymentSchedule,
          t,
          partialPaymentPolicy,
        );
        if (scheduleErr) {
          setError(scheduleErr);
          return;
        }
      }

      const formEl = e.currentTarget;
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      setSubmitting(true);
      setError(null);

      let recaptchaToken = "";
      if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
        if (captchaConfig.provider === "google_recaptcha_v2") {
          recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || "";
          if (!recaptchaToken) {
            setError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
            setSubmitting(false);
            return;
          }
        } else if (captchaConfig.provider === "google_recaptcha_v3" || captchaConfig.provider === "google_recaptcha_enterprise") {
          try {
            await loadRecaptchaV3Script(
              captchaConfig.site_key,
              captchaConfig.provider === "google_recaptcha_enterprise" ? captchaConfig.script_url ?? undefined : undefined
            );
            recaptchaToken = (await getRecaptchaV3Token(captchaConfig.site_key, "reservation_book_now")) || "";
          } catch {
            recaptchaToken = "";
          }
          if (!recaptchaToken) {
            setError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
            setSubmitting(false);
            return;
          }
        } else if (captchaConfig.provider === "cloudflare_turnstile") {
          recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || "";
          if (!recaptchaToken) {
            setError(t("common.captcha_required", language === "tr" ? "Lütfen robot doğrulamayı tamamlayın." : "Please complete the robot verification."));
            setSubmitting(false);
            return;
          }
        }
      }

      const participants = buildParticipantsPayload();
      const roomsPayload = effectiveRoomsForPrice.map((r) => ({
        adults: r.adults ?? 1,
        children: r.children ?? 0,
        childAges: r.childAges ?? [],
      }));
      const normalizedTravelDate = normalizeDepartureDate(effectiveDepartureDate);
      if (normalizedTravelDate === "-") {
        setError(t("reservation.submit_error", "Submit failed"));
        setSubmitting(false);
        return;
      }
      const payload = {
        product_id: productId,
        product_type: cookieSummary.product_type || "tour",
        guest_count: adults + children,
        child_count: children,
        travel_date: normalizedTravelDate,
        check_in_date: normalizedTravelDate,
        check_out_date: undefined,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || undefined,
        tc_no: form.nationality === TURKEY_CODE ? (form.national_id?.trim() || undefined) : undefined,
        passport_no: form.passport_no?.trim() || undefined,
        nationality: form.nationality || undefined,
        participants: participants.length > 0 ? participants : undefined,
        rooms: roomsPayload,
        ...(estimateTotal > 0 ? { total_price: estimateTotal } : {}),
        ...(priceData?.roomPrices?.length
          ? {
              room_prices: priceData.roomPrices.map((rp) => ({
                roomIndex: rp.roomIndex,
                total_price: rp.total_price,
              })),
            }
          : {}),
        ...(periodId !== "-" ? { period_id: Number(periodId) } : {}),
        agreements: Object.entries(agreementsState)
          .filter(([, accepted]) => accepted)
          .map(([id]) => ({ id: parseInt(id, 10), accepted: true })),
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
        ...(reservationFormLanguageId ? { language_id: reservationFormLanguageId } : {}),
        billing_first_name: billingContactDifferent ? billingContact.first_name.trim() : form.first_name.trim(),
        billing_last_name: billingContactDifferent ? billingContact.last_name.trim() : form.last_name.trim(),
        billing_email: billingContactDifferent ? billingContact.email.trim() : form.email.trim(),
        billing_phone: form.phone?.trim() || undefined,
        billing_address: billing.address.trim(),
        billing_city: billing.city.trim(),
        billing_country: billing.country.trim(),
        billing_postal_code: billing.postal_code.trim() || undefined,
        ...(checkoutPayAmount > 0
          ? { payment_mode: paymentModeForSubmit, pay_now_amount: checkoutPayAmount }
          : {}),
        ...(paymentModeForSubmit === "partial" && balanceAfterPayNow > 0.009
          ? {
              payment_schedule: paymentSchedule.map((row) => ({
                amount: Math.round((parseFloat(row.amount) || 0) * 100) / 100,
                due_date: row.due_date,
              })),
            }
          : {}),
        ...(saveCardForFuture && paymentModeForSubmit === "partial"
          ? { save_card_for_future: true }
          : {}),
      };
      try {
        const res = await fetch("/api/reservations/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Accept-Language": language || currentLocale || "en",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        let data: unknown;
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        if (!res.ok) {
          const msg = typeof data === "object" && data != null && "message" in data ? (data as { message?: string }).message : "";
          const isEmailRegistered =
            typeof data === "object" &&
            data != null &&
            "errors" in data &&
            (data as { errors?: { email?: string[] } }).errors?.email != null;
          if (isEmailRegistered || (typeof msg === "string" && /already registered|zaten kayıtlı|email.*exist|e-posta.*kayıtlı/i.test(msg))) {
            setLoginModalEmail(form.email.trim());
            setLoginModalOpen(true);
            setError(t("reservation.email_registered_login_hint", "This email is already registered. Please log in below."));
            setSubmitting(false);
            return;
          }
          const friendlyMsg = getReservationErrorMessage(data, "reservation.submit_error", "Submit failed");
          setError(friendlyMsg);
          setSubmitting(false);
          return;
        }
        const redirectUrl = (data as { data?: { redirect_url?: string } })?.data?.redirect_url;
        const token = redirectUrl ? extractTokenFromRedirectUrl(redirectUrl) : null;

        if (paymentMethod === "card" && token) {
          const cardErrorsBeforePay = validateCardFields();
          if (Object.keys(cardErrorsBeforePay).length > 0) {
            setFieldErrors((prev) => ({ ...prev, ...cardErrorsBeforePay }));
            setError(t("payment.error.card_incomplete", language === "tr" ? "Kart bilgileri eksik." : "Card details are incomplete."));
            setSubmitting(false);
            paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          setSubmitting(false);
          setPaymentProcessing(true);
          setError(null);
          try {
            const result = await paymentService.payWithCard(token, {
              card_holder_name: cardHolder.trim(),
              card_number: cardNumber.replace(/\s/g, ""),
              expire_month: expireMonth,
              expire_year: expireYear,
              cvc,
              installment,
              use_3d: use3D,
              ...(checkoutPayAmount > 0 ? { amount: checkoutPayAmount } : {}),
              ...(currentLocale ? { locale: currentLocale } : {}),
            });
            if (result.requires_3d && result.html_content) {
              setThreeDSHtml(result.html_content);
            } else if (result.success) {
              clearReservationCookies();
              setSuccessKind("card");
              setSuccess(true);
            } else {
              setError(result.message || t("payment.error.failed", "Payment failed"));
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : t("payment.error.failed", "Payment failed"));
          } finally {
            setPaymentProcessing(false);
          }
          return;
        }

        if (paymentMethod === "bank" && token) {
          setSubmitting(false);
          setPaymentProcessing(true);
          setError(null);
          try {
            const result = await paymentService.createBankTransfer(token, {
              bank_account_id: selectedBank!,
              amount: parseFloat(transferAmount),
              transfer_date: transferDate,
              sender_name: senderName.trim(),
              sender_bank: senderBank.trim() || undefined,
              reference_no: referenceNo.trim() || undefined,
              sender_email: senderEmail.trim(),
              sender_phone: senderPhone.trim() || undefined,
              notes: transferNotes.trim() || undefined,
            });
            if (receiptFile && result.notification_id) {
              try {
                await paymentService.uploadReceipt(token, result.notification_id, receiptFile);
              } catch {
                // Dekont yüklenemese bile bildirim başarılı sayılır
              }
            }
            clearReservationCookies();
            setSuccessKind("bank");
            setSuccess(true);
          } catch (err) {
            let msg = t("payment.error.bank_transfer", "Transfer notification could not be created");
            if (err instanceof Error) {
              try {
                const text = err.message;
                if (text.includes("errors") || text.includes("error")) {
                  const parsed = JSON.parse(text);
                  if (parsed?.errors) {
                    const first = Object.values(parsed.errors)[0];
                    msg = Array.isArray(first) ? first[0] : String(first);
                  } else if (parsed?.message) msg = parsed.message;
                } else {
                  msg = text;
                }
              } catch {
                msg = err.message;
              }
            }
            setError(msg);
          } finally {
            setPaymentProcessing(false);
          }
          return;
        }

        if (redirectUrl) {
          if (/^https?:\/\//i.test(redirectUrl)) {
            window.location.href = redirectUrl;
          } else {
            router.push(redirectUrl);
          }
          return;
        }
        const paymentSlug = language === "tr" ? "rezervasyon/odeme" : "payment";
        router.push(`/${language}/${paymentSlug}`);
      } catch (err) {
        let msg = t("reservation.submit_error", "Submit failed");
        if (err instanceof Error && err.message) {
          const raw = err.message.trim();
          if (raw.startsWith("{")) {
            try {
              const parsed = JSON.parse(raw) as unknown;
              msg = getReservationErrorMessage(parsed, "reservation.submit_error", "Submit failed");
            } catch {
              msg = t("reservation.submit_error", "Submit failed");
            }
          } else {
            msg = raw;
          }
        }
        setError(msg);
      } finally {
        setSubmitting(false);
        if (captchaConfig?.provider === "google_recaptcha_v2" && captchaWidgetIdRef.current != null) {
          resetRecaptchaWidget(captchaWidgetIdRef.current as number);
        } else if (captchaConfig?.provider === "cloudflare_turnstile" && captchaWidgetIdRef.current != null) {
          resetTurnstileWidget(captchaWidgetIdRef.current as string);
        }
      }
    },
    [
      hasValidSelection,
      cookieSummary,
      validateForm,
      validateCardFields,
      validateBankTransferForm,
      paymentMethod,
      language,
      cardHolder,
      cardNumber,
      expireMonth,
      expireYear,
      cvc,
      use3D,
      selectedBank,
      transferAmount,
      transferDate,
      senderName,
      senderBank,
      referenceNo,
      senderEmail,
      senderPhone,
      transferNotes,
      receiptFile,
      buildParticipantsPayload,
      effectiveRoomsForPrice,
      productId,
      adults,
      children,
      effectiveDepartureDate,
      periodId,
      estimateTotal,
      checkoutPayAmount,
      paymentModeForSubmit,
      validatePayNowAmount,
      form,
      billing,
      billingContactDifferent,
      billingContact,
      language,
      currentLocale,
      t,
      getVariable,
      agreementsState,
      reservationFormLanguageId,
      isCaptchaRequired,
      captchaConfig,
      getReservationErrorMessage,
      router,
    ]
  );

  const showTc = (nationality: string) => nationality === TURKEY_CODE;

  const handleLoginModalSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!loginModalEmail.trim() || !loginPassword.trim()) return;
      setLoginLoading(true);
      setLoginError(null);
      try {
        await authLogin(loginModalEmail.trim(), loginPassword, true, language);
        await refreshCustomer();
        loginModalWasOpenRef.current = true;
        setLoginModalOpen(false);
        setLoginPassword("");
        setError(null);
      } catch (err) {
        setLoginError(err instanceof Error ? err.message : t("auth.login.failed", "Login failed"));
      } finally {
        setLoginLoading(false);
      }
    },
    [authLogin, refreshCustomer, loginModalEmail, language, t]
  );

  useEffect(() => {
    if (!threeDSHtml) return;
    const attemptSubmit = () => {
      const selectors = ["form[action*='3dsecure']", "form[action*='3d']", "form[method='post']", "#iyzico-3ds-form form", "form"];
      for (const selector of selectors) {
        const formEl = document.querySelector(selector) as HTMLFormElement;
        if (formEl?.action) {
          formEl.submit();
          return true;
        }
      }
      return false;
    };
    if (attemptSubmit()) return;
    const t1 = setTimeout(attemptSubmit, 200);
    const t2 = setTimeout(attemptSubmit, 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [threeDSHtml]);

  if (threeDSHtml) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-6 w-full max-w-4xl">
          <h2 className="text-xl font-semibold mb-4 text-center">{t("payment.3ds.title", "3D Secure Verification")}</h2>
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-alsero-first" />
            <p className="ml-3 text-gray-600">{t("payment.3ds.redirecting", "Redirecting to bank page...")}</p>
          </div>
          <div id="book-now-3ds-form" className="w-full" dangerouslySetInnerHTML={{ __html: threeDSHtml }} style={{ minHeight: "400px", display: "none" }} />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {successKind === "bank"
              ? t("payment.bank_transfer.success_title", "Transfer notification received")
              : t("payment.success.title", "Transaction Successful")}
          </h2>
          <p className="text-gray-600 mb-6">
            {successKind === "bank"
              ? t(
                  "payment.bank_transfer.success_message",
                  "Your bank transfer notification was submitted. We will confirm your payment after verifying the transfer."
                )
              : t("payment.success.completed", "Your payment has been completed successfully!")}
          </p>
          <Link href={`/${language}`} className="inline-block px-6 py-3 rounded-lg font-semibold text-white bg-alsero-first hover:opacity-90">
            {t("reservation.browse_tours", "Browse tours")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
    <section className="min-h-screen bg-neutral-100 py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
          {/* Sol: Ana içerik (TourRadar "Your adventure overview" tarzı) */}
          <div className="min-w-0">
            <h1 className="mb-1 text-2xl font-bold text-gray-900 md:text-3xl">
              {displayProductName || t("tour_detail.confirm_dates", "Confirm Dates")}
            </h1>
            <p className="mb-6 text-sm text-gray-600">
              {t("tour_detail.book_now_summary", "Review your selected departure and continue to checkout.")}
            </p>

            {!hasMounted ? (
              <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-500">{language === "tr" ? "Yükleniyor..." : "Loading..."}</p>
              </div>
            ) : !hasValidSelection ? (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm">
                {t("tour_detail.book_now_missing_selection", "Missing booking selection. Please choose a departure date again.")}
                <Link href={backToDatesHref} prefetch={false} className="ml-2 font-semibold text-amber-900 underline">
                  {t("tour_detail.back_to_departure_dates", "Back to Departure Dates")}
                </Link>
              </div>
            ) : (
            <>
              <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">1</span>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("tour_detail.your_adventure_overview", "Your adventure overview")}
                  </h2>
                </div>
                <div className="mt-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                    <span className="font-medium text-gray-800">{t("tour_detail.departure_date", "Departure Date")}:</span>
                    {!availableDepartureDatesLoading && availableDepartureDates.length > 0 ? (
                      <span className="font-semibold text-gray-900">{formattedDepartureDate}</span>
                    ) : availableDepartureDatesLoading ? (
                      <span className="text-gray-500">{language === "tr" ? "Yükleniyor..." : "Loading..."}</span>
                    ) : (
                      <span className="font-semibold text-gray-900">{formattedDepartureDate}</span>
                    )}
                  </div>
                  {availableDepartureDates.length > 0 && availableMonths.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 space-y-3">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {language === "tr" ? "Aya göre fiyat ve çıkış tarihleri" : "Prices & departure dates by month"}
                      </label>
                      <select
                        value={selectedMonthForCalendar}
                        onChange={(e) => setSelectedMonthForCalendar(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:border-alsero-first focus:outline-none focus:ring-1 focus:ring-alsero-first w-full max-w-[200px]"
                        aria-label={language === "tr" ? "Ay seçin" : "Select month"}
                      >
                        {availableMonths.map((ym) => {
                          const [y, m] = ym.split("-").map(Number);
                          const date = new Date(y, m - 1, 1);
                          const label = date.toLocaleDateString(locale, { month: "long", year: "numeric" });
                          return (
                            <option key={ym} value={ym}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                      {datesInSelectedMonth.length === 0 ? (
                        monthPricesLoading ? (
                          <p className="text-sm text-gray-500 py-2">{language === "tr" ? "Fiyatlar yükleniyor..." : "Loading prices..."}</p>
                        ) : (
                          <p className="text-sm text-gray-500 py-2">{language === "tr" ? "Bu ayda çıkış tarihi yok." : "No departures this month."}</p>
                        )
                      ) : (
                        <ul className="space-y-2 max-h-[240px] overflow-y-auto">
                          {datesInSelectedMonth.map((d) => {
                            const isSelected = d.departure_date === effectiveDepartureDate;
                            const dateLabel = new Date(d.departure_date).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
                            const priceStr =
                              d.price_per_person != null && Number.isFinite(d.price_per_person)
                                ? `${displayCurrencySymbol}${Number(d.price_per_person).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : null;
                            const originalPriceStr =
                              d.original_price_per_person != null &&
                              Number.isFinite(d.original_price_per_person) &&
                              d.price_per_person != null &&
                              d.original_price_per_person > d.price_per_person
                                ? `${displayCurrencySymbol}${Number(d.original_price_per_person).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : null;
                            return (
                              <li key={d.departure_date}>
                                <button
                                  type="button"
                                  onClick={() => handleDepartureDateChange(d.departure_date)}
                                  className={`w-full flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-colors ${
                                    isSelected
                                      ? "border-alsero-first bg-alsero-first/10 text-gray-900"
                                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-gray-800"
                                  }`}
                                >
                                  <span className="font-medium">{dateLabel}</span>
                                  <span className="flex flex-col items-end gap-0.5">
                                    {d.appliedCampaignName && (
                                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 w-fit">
                                        {d.appliedCampaignName}
                                      </span>
                                    )}
                                    {!d.appliedCampaignName && d.isDiscounted && (
                                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 w-fit">
                                        {language === "tr" ? "İndirimli" : "Discounted"}
                                      </span>
                                    )}
                                    <span className="flex items-center gap-2">
                                      {originalPriceStr && <span className="text-xs text-gray-400 line-through">{originalPriceStr}</span>}
                                      {priceStr && <span className="font-semibold text-alsero-dark-gray">{priceStr}</span>}
                                      {!priceStr && <span className="text-gray-400">—</span>}
                                    </span>
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-alsero-first font-bold">•</span>
                    {t("tour_detail.guaranteed_departure", "Guaranteed departure")}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-alsero-first font-bold">•</span>
                    {adults} {adults === 1 ? t("tour_detail.adult", "Adult") : t("tour_detail.adults", "Adults")}
                    {children > 0 && `, ${children} ${children === 1 ? t("tour_detail.child", "Child") : t("tour_detail.children", "Children")}`}
                  </li>
                </ul>
              </div>

              {/* Misafir sayısı – book-now sayfasına özel kompakt tasarım */}
              <div className="mt-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">2</span>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {language === "tr" ? "Kimler seyahat ediyor?" : "Who's traveling?"}
                  </h2>
                </div>
                <div className="py-5">
                  <p className="mt-3 mb-2 text-sm text-gray-500">
                    {t("tour_detail.number_of_guests", "Number of guests")}
                  </p>
                  <p className="mb-4 text-xs text-gray-400">
                    {language === "tr"
                      ? "Oda başına en fazla 3 kişi (en fazla 3 yetişkin; 3 yetişkin seçildiğinde çocuk eklenemez)."
                      : "Max 3 persons per room (max 3 adults; when 3 adults are selected, children cannot be added)."}
                  </p>
                  {(productMetas?.min_group_size != null || productMetas?.max_group_size != null || productMetas?.min_ages != null) && (
                    <p className="mb-4 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                      {productMetas?.min_group_size != null && (
                        <span>{t("tour_detail.min_group_size_hint", "Min. group size")}: {productMetas.min_group_size}. </span>
                      )}
                      {productMetas?.max_group_size != null && (
                        <span>{t("tour_detail.max_group_size_hint", "Max. group size")}: {productMetas.max_group_size}. </span>
                      )}
                      {productMetas?.min_ages != null && productMetas.min_ages !== "" && (
                        <span>{t("tour_detail.minimum_age", "Min. age")}: {String(productMetas.min_ages)}.</span>
                      )}
                    </p>
                  )}
                  {!meetsMinGroup && minGroupSize != null && (
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {t("reservation.min_pax_warning", "Minimum :min person booking required.").replace(/:min/g, String(minGroupSize))}
                    </div>
                  )}
                  {!meetsMaxGroup && maxGroupSize != null && (
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {t("reservation.max_group_warning", "Maximum :max travelers allowed.").replace(/:max/g, String(maxGroupSize))}
                    </div>
                  )}
                  {isGroupTourBooking && departureRemainingPax != null && departureRemainingPax > 0 && departureRemainingPax <= 5 && (
                    <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
                      {t("tour_detail.limited_availability", "Limited availability")}
                      {language === "tr"
                        ? ` — Bu çıkış için en fazla ${departureRemainingPax} kişi rezerve edilebilir.`
                        : ` — Up to ${departureRemainingPax} seat(s) left on this departure.`}
                    </div>
                  )}
                  {isGroupTourBooking && !meetsDepartureQuota && effectiveMaxTravelers != null && (
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
                      {t(
                        "reservation.departure_quota_exceeded",
                        "Not enough seats available for this departure. Reduce the number of guests or choose another date.",
                      )}
                    </div>
                  )}
                  {isGroupTourBooking && !selectedDepartureBookable && (
                    <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
                      {t(
                        "reservation.departure_not_bookable",
                        "This departure is no longer available. Please choose another date.",
                      )}
                    </div>
                  )}
                  <div className="space-y-4">
                    {(rooms.length > 0 ? rooms : [{ adults: 2, children: 0, childAges: [] }]).map((room, index) => (
                      <div
                        key={index}
                        className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4"
                      >
                        <span className="w-16 shrink-0 text-sm font-medium text-gray-700">
                          {language === "tr" ? `Oda ${index + 1}` : `Room ${index + 1}`}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 w-14">{t("tour_detail.adults", "Adults")}</span>
                          <div className="flex items-center rounded-full border-2 border-gray-200 bg-white">
                            <button
                              type="button"
                              onClick={() => {
                                const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                                const r = { ...list[index], adults: Math.max(1, (list[index].adults ?? 1) - 1) };
                                list[index] = r;
                                handleRoomsChange(list);
                              }}
                              disabled={(room.adults ?? 1) <= 1}
                              className="flex h-9 w-9 items-center justify-center rounded-l-full text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">
                                {language === "tr" ? "Yetişkin azalt" : "Decrease adults"}
                              </span>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                            </button>
                            <span className="min-w-[2.25rem] text-center text-sm font-semibold text-gray-900">
                              {room.adults ?? 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                                const cur = list[index].adults ?? 1;
                                const r = { ...list[index], adults: Math.min(MAX_ADULTS_PER_ROOM, cur + 1) };
                                if (r.adults === MAX_ADULTS_PER_ROOM) {
                                  r.children = 0;
                                  r.childAges = [];
                                }
                                list[index] = r;
                                handleRoomsChange(list);
                              }}
                              disabled={
                                (room.adults ?? 1) >= MAX_ADULTS_PER_ROOM ||
                                (effectiveMaxTravelers != null && totalTravelers >= effectiveMaxTravelers)
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-r-full text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">
                                {language === "tr" ? "Yetişkin artır" : "Increase adults"}
                              </span>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm text-gray-600 w-14 ${(room.adults ?? 0) >= MAX_ADULTS_PER_ROOM ? "opacity-60" : ""}`}>
                            {t("tour_detail.children", "Children")}
                          </span>
                          <div className="flex items-center rounded-full border-2 border-gray-200 bg-white">
                            <button
                              type="button"
                              onClick={() => {
                                const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                                const r = { ...list[index], children: Math.max(0, (list[index].children ?? 0) - 1), childAges: (list[index].childAges ?? []).slice(0, Math.max(0, (list[index].children ?? 0) - 1)) };
                                list[index] = r;
                                handleRoomsChange(list);
                              }}
                              className="flex h-9 w-9 items-center justify-center rounded-l-full text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                              disabled={(room.children ?? 0) <= 0 || (room.adults ?? 0) >= MAX_ADULTS_PER_ROOM}
                            >
                              <span className="sr-only">
                                {language === "tr" ? "Çocuk azalt" : "Decrease children"}
                              </span>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                            </button>
                            <span className="min-w-[2.25rem] text-center text-sm font-semibold text-gray-900">
                              {room.children ?? 0}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                                const ra = list[index].adults ?? 1;
                                const rc = list[index].children ?? 0;
                                const maxCh = ra >= MAX_ADULTS_PER_ROOM ? 0 : MAX_PERSONS_PER_ROOM - ra;
                                const r = { ...list[index], children: Math.min(maxCh, rc + 1), childAges: [...(list[index].childAges ?? []).slice(0, rc), 0].slice(0, Math.min(maxCh, rc + 1)) };
                                list[index] = r;
                                handleRoomsChange(list);
                              }}
                              disabled={
                                (room.adults ?? 0) >= MAX_ADULTS_PER_ROOM ||
                                ((room.adults ?? 0) + (room.children ?? 0)) >= MAX_PERSONS_PER_ROOM ||
                                (effectiveMaxTravelers != null && totalTravelers >= effectiveMaxTravelers)
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-r-full text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">
                                {language === "tr" ? "Çocuk artır" : "Increase children"}
                              </span>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            </button>
                          </div>
                        </div>
                        {(rooms.length > 0 ? rooms : [{ adults: 2, children: 0, childAges: [] }]).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                              const next = list.filter((_, i) => i !== index);
                              handleRoomsChange(next.length > 0 ? next : [{ adults: 1, children: 0, childAges: [] }]);
                            }}
                            className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600"
                            aria-label={language === "tr" ? "Odayı kaldır" : "Remove room"}
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </div>
                    ))}
                    {canAddAnotherRoom && (
                      <button
                        type="button"
                        onClick={() => {
                          const list = rooms.length > 0 ? [...rooms] : [{ adults: 2, children: 0, childAges: [] }];
                          handleRoomsChange([...list, { adults: 1, children: 0, childAges: [] }]);
                        }}
                        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-3 text-sm font-medium text-gray-600 hover:border-alsero-first hover:bg-alsero-first/5 hover:text-alsero-first disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!canAddAnotherRoom}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        {language === "tr" ? "Oda ekle" : "Add room"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 rounded-xl border border-gray-100 bg-white p-5 pb-0 shadow-sm">
                <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">3</span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("tour_detail.price_breakdown", "Price breakdown")}
                  </h3>
                </div>
                <div className="py-5">
                  {hasPriceFromApi && priceData?.roomPrices && priceData.roomPrices.length > 0 ? (
                    <>
                      {(priceData as GroupPriceResult).roomBreakdowns?.length
                        ? (priceData as GroupPriceResult).roomBreakdowns.map((rb) => {
                            const room = effectiveRoomsForPrice[rb.roomIndex];
                            const roomLabel = language === "tr" ? `Oda ${rb.roomIndex + 1}` : `Room ${rb.roomIndex + 1}`;
                            const roomPax = room ? (room.adults ?? 0) + (room.children ?? 0) : 0;
                            return (
                              <div key={rb.roomIndex} className="border-b border-gray-100 pb-2 mb-2 last:border-0 last:mb-0 last:pb-0">
                                <div className="flex justify-between text-sm font-medium text-gray-800 mb-1">
                                  <span>{roomLabel}{roomPax > 0 ? ` (${roomPax} ${roomPax === 1 ? (language === "tr" ? "kişi" : "pax") : language === "tr" ? "kişi" : "pax"})` : ""}</span>
                                  <span className="text-alsero-dark-gray">{formatMoney(rb.total_price)}</span>
                                </div>
                                <ul className="space-y-0.5 pl-3 text-xs text-gray-600">
                                  {rb.lines.map((line, idx) => (
                                    <li key={idx} className="flex justify-between">
                                      <span>
                                        {line.count > 1 ? `${line.count} × ` : ""}
                                        {line.type === "per_person" && (line.count === 2 ? t("tour_detail.per_person_double", "Per person (double)") : t("tour_detail.per_person", "Per person"))}
                                        {line.type === "single" && t("tour_detail.single_supplement", "Single supplement")}
                                        {line.type === "extra_bed" && t("tour_detail.extra_bed", "Extra bed")}
                                        {line.type === "child" && (line.ageRange ? t("tour_detail.child_age_range", "Child (age :range)").replace(":range", line.ageRange) : t("tour_detail.child_fee", "Child"))}
                                      </span>
                                      <span>{formatMoney(line.amount)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })
                        : priceData.roomPrices.map((rp) => {
                            const room = effectiveRoomsForPrice[rp.roomIndex];
                            const roomLabel = language === "tr" ? `Oda ${rp.roomIndex + 1}` : `Room ${rp.roomIndex + 1}`;
                            const roomPax = room ? (room.adults ?? 0) + (room.children ?? 0) : 0;
                            return (
                              <div key={rp.roomIndex} className="flex justify-between py-1 text-sm">
                                <span className="text-gray-700">
                                  {roomLabel}
                                  {roomPax > 0 && ` (${roomPax} ${roomPax === 1 ? (language === "tr" ? "kişi" : "pax") : language === "tr" ? "kişi" : "pax"})`}
                                </span>
                                <span className="font-medium text-alsero-dark-gray">{formatMoney(rp.total_price)}</span>
                              </div>
                            );
                          })}
                      <div className="mt-3 flex justify-between border-t border-gray-200 pt-3 font-semibold text-alsero-dark-gray">
                        <span>{t("tour_detail.estimated_total", "Estimated total")}</span>
                        <span>{formatMoney(priceData.total_price)}</span>
                      </div>
                    </>
                  ) : showFromPrice ? (
                    <div className="space-y-2">
                      <div className="flex justify-between py-1 text-sm font-semibold text-alsero-dark-gray">
                        <span>{language === "tr" ? "Başlangıç fiyatı" : "From"}</span>
                        <span>{formatMoney(minFromPrice)}</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t("tour_detail.select_departure_for_exact_price", "Select a departure date to see exact price.")}
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500">
                    {t("tour_detail.price_will_be_shown_checkout", "Price details will be finalized at checkout.")}
                    </p>
                  )}
                </div>
              </div>
              {/* Yolcu bilgileri + ödeme */}
              <form id="book-now-checkout-form" onSubmit={handleSubmit} className="mt-8 space-y-6">
                {error && Object.keys(fieldErrors).length > 0 && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    <p className="font-semibold">{t("reservation.fix_form_errors", "Please check the highlighted fields below.")}</p>
                  </div>
                )}

                <div ref={contactSectionRef} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">4</span>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t("reservation.contact_person_details", "Contact person details")}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t("reservation.contact_person_hint", "Main contact for this booking — not your invoice address.")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.first_name", "First name")} *</label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={(e) => {
                          clearFieldError("first_name");
                          setForm((f) => ({ ...f, first_name: e.target.value }));
                        }}
                        className={bookNowFieldClass(!!fieldErrors.first_name)}
                        autoComplete="given-name"
                        required
                      />
                      {fieldErrors.first_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.first_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.last_name", "Last name")} *</label>
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={(e) => {
                          clearFieldError("last_name");
                          setForm((f) => ({ ...f, last_name: e.target.value }));
                        }}
                        className={bookNowFieldClass(!!fieldErrors.last_name)}
                        autoComplete="family-name"
                        required
                      />
                      {fieldErrors.last_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.last_name}</p>}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.email", "Email")} *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => {
                        clearFieldError("email");
                        setForm((f) => ({ ...f, email: e.target.value }));
                      }}
                      className={bookNowFieldClass(!!fieldErrors.email)}
                      autoComplete="email"
                      required
                    />
                    {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      {t("reservation.phone", "Phone")}{" "}
                      <span className="font-normal text-gray-500">({t("common.optional", "optional")})</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => {
                        clearFieldError("phone");
                        setForm((f) => ({ ...f, phone: e.target.value }));
                      }}
                      className={bookNowFieldClass(!!fieldErrors.phone)}
                      autoComplete="tel"
                      placeholder="+1 555 000 0000"
                    />
                    {fieldErrors.phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <NationalitySelect value={form.nationality} onChange={(v) => setForm((f) => ({ ...f, nationality: v }))} label={t("reservation.nationality", "Nationality")} />
                    {showTc(form.nationality) && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.national_id", "ID number")}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={11}
                          value={form.national_id}
                          onChange={(e) => setForm((f) => ({ ...f, national_id: e.target.value.replace(/\D/g, "") }))}
                          className={bookNowFieldClass()}
                        />
                      </div>
                    )}
                    <div className={showTc(form.nationality) ? "" : "sm:col-span-2"}>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.passport_no", "Passport no")}</label>
                      <input
                        type="text"
                        value={form.passport_no}
                        onChange={(e) => setForm((f) => ({ ...f, passport_no: e.target.value }))}
                        className={bookNowFieldClass()}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  {!isAuthenticated && (
                    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3">
                      <p className="text-sm text-gray-700">
                        {language === "tr"
                          ? "E-postanız kayıtlı değilse rezervasyon sonrası hesap açılır; giriş linki e-postanıza gelir."
                          : "If your email is new, we create an account after booking and email you a sign-in link."}
                      </p>
                      <Link
                        href={`/${language}/register`}
                        className="mt-2 inline-block text-sm font-semibold text-alsero-first hover:underline"
                      >
                        {language === "tr" ? "Zaten üye misiniz? Giriş / üye ol" : "Already have an account? Sign in / register"}
                      </Link>
                    </div>
                  )}
                </div>

                <div ref={billingSectionRef} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5 mb-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">5</span>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {t("reservation.billing_invoice_details", "Billing / invoice address")}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t(
                          "reservation.billing_invoice_hint",
                          "For your payment receipt. Use your home address or hotel name abroad.",
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <CountryCitySelect
                        countries={countries}
                        countryValue={billing.country || null}
                        cityValue={billing.city || null}
                        onCountryChange={(_, countryCode) => {
                          clearFieldError("billing_country");
                          setBilling((b) => ({ ...b, country: countryCode || "" }));
                        }}
                        onCityChange={(_, cityName) => {
                          clearFieldError("billing_city");
                          setBilling((b) => ({ ...b, city: cityName || "" }));
                        }}
                        countryPlaceholder={t("reservation.billing_country", "Country") + " *"}
                        cityPlaceholder={t("reservation.billing_city", "City") + " *"}
                      />
                      {(fieldErrors.billing_country || fieldErrors.billing_city) && (
                        <p className="mt-1 text-xs text-red-600">
                          {fieldErrors.billing_country || fieldErrors.billing_city}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">
                        {t("reservation.billing_address", "Address")} *
                      </label>
                      <textarea
                        value={billing.address}
                        onChange={(e) => {
                          clearFieldError("billing_address");
                          setBilling((b) => ({ ...b, address: e.target.value }));
                        }}
                        rows={2}
                        className={`${bookNowFieldClass(!!fieldErrors.billing_address)} text-sm resize-y min-h-[72px]`}
                        placeholder={t(
                          "reservation.billing_address_placeholder",
                          "Street, hotel name, or accommodation address",
                        )}
                        autoComplete="street-address"
                      />
                      {fieldErrors.billing_address && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors.billing_address}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-1">
                        {t("reservation.billing_postal_code", "Postal code")}{" "}
                        <span className="font-normal text-gray-500">({t("common.optional", "optional")})</span>
                      </label>
                      <input
                        type="text"
                        value={billing.postal_code}
                        onChange={(e) => setBilling((b) => ({ ...b, postal_code: e.target.value }))}
                        className={bookNowFieldClass()}
                        autoComplete="postal-code"
                      />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={billingContactDifferent}
                        onChange={(e) => setBillingContactDifferent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-alsero-first focus:ring-alsero-first"
                      />
                      <span className="text-sm text-gray-800">
                        {t(
                          "reservation.billing_different_contact",
                          "Invoice name or email is different from the contact person above",
                        )}
                      </span>
                    </label>

                    {billingContactDifferent && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-dashed border-gray-200 bg-white p-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.first_name", "First name")} *</label>
                          <input
                            type="text"
                            value={billingContact.first_name}
                            onChange={(e) => {
                              clearFieldError("billing_first_name");
                              setBillingContact((c) => ({ ...c, first_name: e.target.value }));
                            }}
                            className={bookNowFieldClass(!!fieldErrors.billing_first_name)}
                          />
                          {fieldErrors.billing_first_name && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.billing_first_name}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.last_name", "Last name")} *</label>
                          <input
                            type="text"
                            value={billingContact.last_name}
                            onChange={(e) => {
                              clearFieldError("billing_last_name");
                              setBillingContact((c) => ({ ...c, last_name: e.target.value }));
                            }}
                            className={bookNowFieldClass(!!fieldErrors.billing_last_name)}
                          />
                          {fieldErrors.billing_last_name && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.billing_last_name}</p>
                          )}
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-semibold text-gray-800 mb-1">{t("reservation.email", "Email")} *</label>
                          <input
                            type="email"
                            value={billingContact.email}
                            onChange={(e) => {
                              clearFieldError("billing_email");
                              setBillingContact((c) => ({ ...c, email: e.target.value }));
                            }}
                            className={bookNowFieldClass(!!fieldErrors.billing_email)}
                          />
                          {fieldErrors.billing_email && (
                            <p className="mt-1 text-xs text-red-600">{fieldErrors.billing_email}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {participantsByRoom.length > 0 && (
                    <div ref={participantsSectionRef} className="rounded-xl border border-gray-100 bg-white p-5 pb-0 shadow-sm">
                      <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">6</span>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t("reservation.room_guest_details", "Guest details")}
                        </h2>
                      </div>
                      <div className="py-5">
                        {participantsByRoom.map((travelers, roomIndex) => (
                          <div key={roomIndex} className={roomIndex > 0 ? "mt-6 pt-6 border-t-2 border-gray-200" : ""}>
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                              {t("reservation.room_number", "Room")} {roomIndex + 1}
                            </p>
                            {travelers.map((p, guestIndex) => (
                              <div key={guestIndex} className="mb-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                                <p className="text-xs font-semibold text-gray-600 mb-2">
                                  {p.type === "adult" ? t("reservation.adult", "Adult") : t("reservation.child", "Child")} {guestIndex + 1}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                  <div>
                                    <input
                                      type="text"
                                      placeholder={t("reservation.first_name", "First name")}
                                      value={p.first_name}
                                      required
                                      onChange={(e) => {
                                        clearFieldError(participantFieldKey(roomIndex, guestIndex, "first_name"));
                                        setParticipantsByRoom((prev) =>
                                          prev.map((r, ri) =>
                                            ri === roomIndex
                                              ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, first_name: e.target.value } : trav))
                                              : r,
                                          ),
                                        );
                                      }}
                                      className={bookNowFieldClass(!!fieldErrors[participantFieldKey(roomIndex, guestIndex, "first_name")])}
                                    />
                                    {fieldErrors[participantFieldKey(roomIndex, guestIndex, "first_name")] && (
                                      <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, "first_name")]}</p>
                                    )}
                                  </div>
                                  <div>
                                    <input
                                      type="text"
                                      placeholder={t("reservation.last_name", "Last name")}
                                      value={p.last_name}
                                      required
                                      onChange={(e) => {
                                        clearFieldError(participantFieldKey(roomIndex, guestIndex, "last_name"));
                                        setParticipantsByRoom((prev) =>
                                          prev.map((r, ri) =>
                                            ri === roomIndex
                                              ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, last_name: e.target.value } : trav))
                                              : r,
                                          ),
                                        );
                                      }}
                                      className={bookNowFieldClass(!!fieldErrors[participantFieldKey(roomIndex, guestIndex, "last_name")])}
                                    />
                                    {fieldErrors[participantFieldKey(roomIndex, guestIndex, "last_name")] && (
                                      <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, "last_name")]}</p>
                                    )}
                                  </div>
                                  <div className="sm:col-span-2 lg:col-span-1">
                                    <NationalitySelect
                                      value={p.nationality}
                                      onChange={(v) =>
                                        setParticipantsByRoom((prev) =>
                                          prev.map((r, ri) => (ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, nationality: v } : trav)) : r))
                                        )
                                      }
                                      label=""
                                    />
                                  </div>
                                  {showTc(p.nationality) && (
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      maxLength={11}
                                      placeholder={t("reservation.national_id", "ID")}
                                      value={p.national_id}
                                      onChange={(e) =>
                                        setParticipantsByRoom((prev) =>
                                          prev.map((r, ri) =>
                                            ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, national_id: e.target.value.replace(/\D/g, "") } : trav)) : r
                                          )
                                        )
                                    }
                                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first"
                                    />
                                  )}
                                  <input
                                    type="text"
                                    placeholder={t("reservation.passport_no", "Passport")}
                                    value={p.passport_no}
                                    onChange={(e) =>
                                      setParticipantsByRoom((prev) =>
                                        prev.map((r, ri) => (ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, passport_no: e.target.value } : trav)) : r))
                                      )
                                    }
                                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first"
                                  />
                                  {p.type === "child" && (
                                    <div className="sm:col-span-2">
                                      <label className="block text-xs text-gray-600 mb-1">{t("reservation.child_birthdate", "Birth date")}</label>
                                      <input
                                        type="date"
                                        value={p.birth_date || ""}
                                        onChange={(e) =>
                                          setParticipantsByRoom((prev) =>
                                            prev.map((r, ri) => (ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, birth_date: e.target.value } : trav)) : r))
                                          )
                                        }
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                <div ref={paymentSectionRef} className="rounded-xl border border-gray-100 bg-white p-5 pb-8 sm:pb-10 shadow-sm space-y-6">
                  <div className="flex items-center gap-3 rounded-t-xl border-b border-gray-100 bg-gray-50 px-4 py-3 -mx-5 -mt-5 mb-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alsero-first text-sm font-bold text-white">
                      {participantsByRoom.length > 0 ? 7 : 6}
                    </span>
                    <h2 className="text-lg font-semibold text-gray-900">{t("payment.method", "Payment")}</h2>
                  </div>
                <PaymentMethodSection
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={setPaymentMethod}
                  cardValue={{
                    cardHolder,
                    cardNumber,
                    expireMonth,
                    expireYear,
                    cvc,
                    use3D,
                  }}
                  cardFieldErrors={{
                    card_holder: fieldErrors.card_holder,
                    card_number: fieldErrors.card_number,
                    card_expiry: fieldErrors.card_expiry,
                    card_cvc: fieldErrors.card_cvc,
                  }}
                  onCardChange={(next) => {
                    if (next.cardHolder !== undefined) {
                      clearFieldError("card_holder");
                      setCardHolder(next.cardHolder);
                    }
                    if (next.cardNumber !== undefined) {
                      clearFieldError("card_number");
                      setCardNumber(next.cardNumber);
                    }
                    if (next.expireMonth !== undefined) {
                      clearFieldError("card_expiry");
                      setExpireMonth(next.expireMonth);
                    }
                    if (next.expireYear !== undefined) {
                      clearFieldError("card_expiry");
                      setExpireYear(next.expireYear);
                    }
                    if (next.cvc !== undefined) {
                      clearFieldError("card_cvc");
                      setCvc(next.cvc);
                    }
                    if (next.use3D !== undefined) setUse3D(next.use3D);
                  }}
                  bankValue={{
                    selectedBank,
                    transferAmount,
                    transferDate,
                    senderName,
                    senderBank,
                    referenceNo,
                    senderEmail,
                    senderPhone,
                    notes: transferNotes,
                    receiptFile,
                  }}
                  onBankChange={(next) => {
                    if (next.selectedBank !== undefined) setSelectedBank(next.selectedBank);
                    if (next.transferAmount !== undefined) setTransferAmount(next.transferAmount);
                    if (next.transferDate !== undefined) setTransferDate(next.transferDate);
                    if (next.senderName !== undefined) setSenderName(next.senderName);
                    if (next.senderBank !== undefined) setSenderBank(next.senderBank);
                    if (next.referenceNo !== undefined) setReferenceNo(next.referenceNo);
                    if (next.senderEmail !== undefined) setSenderEmail(next.senderEmail);
                    if (next.senderPhone !== undefined) setSenderPhone(next.senderPhone);
                    if (next.notes !== undefined) setTransferNotes(next.notes);
                    if (next.receiptFile !== undefined) setReceiptFile(next.receiptFile);
                  }}
                  paymentOptions={paymentOptions}
                  paymentOptionsLoading={paymentOptionsLoading}
                  t={t}
                  language={language}
                  onLoadTestCard={loadTestCard}
                  showCardAnimation={true}
                  installments={installments}
                  installment={installment}
                  onInstallmentChange={setInstallment}
                  installmentSectionTitle={t("payment.bank_installment", "Bank installment")}
                  currencySymbol={productCurrency?.symbol ?? productCurrency?.code ?? "₺"}
                  bankPaymentOnThisScreen={true}
                  variant="tabs"
                  bankMaxAmount={partialPaymentBounds?.max_amount ?? (estimateTotal > 0 ? estimateTotal : undefined)}
                  bankMinAmount={partialPaymentActive && partialPaymentBounds ? partialPaymentBounds.min_amount : undefined}
                />

                {partialPaymentOffered && estimateTotal > 0 && (
                  <div className="border-t border-gray-200 pt-6 space-y-5">
                    <label
                      htmlFor="book-now-wants-partial-payment"
                      className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/70 p-4 transition-colors hover:border-alsero-first/30 hover:bg-alsero-first/[0.03]"
                    >
                      <input
                        id="book-now-wants-partial-payment"
                        type="checkbox"
                        checked={wantsPartialPayment}
                        onChange={(e) => handleWantsPartialPaymentChange(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-400 text-alsero-first focus:ring-alsero-first"
                      />
                      <span className="flex-1">
                        <span className="block text-sm font-semibold text-gray-900">
                          {t(
                            "payment.partial.opt_in",
                            language === "tr" ? "Kısmi ödemek istiyorum" : "I want to pay partially",
                          )}
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-gray-600">
                          {t(
                            "payment.partial.opt_in_hint",
                            language === "tr"
                              ? "Şimdi bir kısmını ödeyip kalan tutar için vade planı oluşturabilirsiniz. İşaretli değilse tur bedelinin tamamı tahsil edilir."
                              : "Pay part of the total now and schedule the rest by due date. If unchecked, the full tour price is charged.",
                          )}
                        </span>
                      </span>
                    </label>

                    {partialPaymentActive && partialPaymentBounds && (
                      <>
                        <PartialPaymentAmountBlock
                          bounds={partialPaymentBounds}
                          currencyCode={productCurrency?.code ?? "TRY"}
                          currencySymbol={productCurrency?.symbol ?? productCurrency?.code ?? "₺"}
                          value={payNowAmount}
                          onChange={(v) => {
                            setPayNowAmount(v);
                            if (paymentMethod === "bank") setTransferAmount(v);
                          }}
                          t={t}
                          language={language}
                        />
                        {paymentModeForSubmit === "partial" &&
                          balanceAfterPayNow > 0.009 &&
                          partialPaymentPolicy && (
                            <PaymentSchedulePlanner
                              balanceAmount={balanceAfterPayNow}
                              policy={partialPaymentPolicy}
                              currencySymbol={productCurrency?.symbol ?? productCurrency?.code ?? "₺"}
                              value={paymentSchedule}
                              onChange={setPaymentSchedule}
                              t={t}
                              locale={language}
                              departureDate={normalizedEffectiveDeparture}
                            />
                          )}
                        {paymentModeForSubmit === "partial" &&
                          paymentMethod === "card" &&
                          cardStorageAvailable && (
                          <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-gray-800">
                            <input
                              type="checkbox"
                              checked={saveCardForFuture}
                              onChange={(e) => setSaveCardForFuture(e.target.checked)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-400 text-alsero-first focus:ring-alsero-first"
                            />
                            <span>
                              {t(
                                "payment.partial.save_card",
                                language === "tr"
                                  ? "Vade ödemeleri için kartımı kaydetmek istiyorum"
                                  : "Save my card for future installment payments",
                              )}
                            </span>
                          </label>
                        )}
                      </>
                    )}
                  </div>
                )}
                </div>

                {/* Sözleşmeler – TourRadar tarzı: zorunlular link metni + tek onay, opsiyoneller checkbox+link */}
                {agreements.length > 0 && (
                  <div className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
                    <h4 className="text-gray-900 text-sm font-semibold">{t("reservation.agreements", "Agreements")}</h4>
                    {requiredAgreements.length > 0 && (
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="book-now-accept-required"
                          checked={requiredAgreements.every((a) => agreementsState[a.id] === true)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAgreementsState((prev) => {
                              const next = { ...prev };
                              requiredAgreements.forEach((a) => (next[a.id] = checked));
                              return next;
                            });
                          }}
                          className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-gray-400 rounded cursor-pointer"
                        />
                        <label htmlFor="book-now-accept-required" className="flex-1 text-sm text-gray-800 cursor-pointer">
                          {language === "tr" ? "Kabul ediyorum: " : "I accept "}
                          {requiredAgreements.map((agreement, index) => (
                            <span key={agreement.id}>
                              <button
                                type="button"
                                onClick={() => handleViewAgreement(agreement)}
                                className="text-alsero-first hover:underline font-semibold"
                              >
                                {agreement.title}
                              </button>
                              {index < requiredAgreements.length - 1 ? (language === "tr" ? "; " : "; ") : ""}
                            </span>
                          ))}
                          {language === "tr" ? "." : "."}
                        </label>
                      </div>
                    )}
                    {optionalAgreements.map((agreement) => (
                      <div key={agreement.id} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id={`agreement-opt-${agreement.id}`}
                          checked={agreementsState[agreement.id] ?? false}
                          onChange={(e) => handleAgreementChange(agreement.id, e.target.checked)}
                          className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-gray-400 rounded cursor-pointer"
                        />
                        <label htmlFor={`agreement-opt-${agreement.id}`} className="flex-1 text-sm text-gray-700 cursor-pointer">
                          <button
                            type="button"
                            onClick={() => handleViewAgreement(agreement)}
                            className="text-alsero-first hover:underline font-semibold"
                          >
                            {agreement.title}
                          </button>
                          {t("request_form.agreement_optional_confirm", language === "tr" ? "'ni okudum, onaylıyorum." : " – I have read and accept.")}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {isCaptchaRequired && (captchaConfig?.provider === "google_recaptcha_v2" || captchaConfig?.provider === "cloudflare_turnstile") && (
                  <div id={BOOK_NOW_CAPTCHA_CONTAINER_ID} className="min-h-[78px]" />
                )}

                {error && (
                  <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 text-sm font-medium text-red-800">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || paymentProcessing || captchaConfigLoading}
                  className="w-full py-3.5 rounded-xl font-semibold text-white bg-alsero-first hover:bg-alsero-first/90 disabled:opacity-60 disabled:cursor-not-allowed shadow-md border-2 border-alsero-first/80"
                >
                  {paymentProcessing
                    ? t("payment.processing", "Processing...")
                    : submitting
                      ? t("reservation.submitting", "Submitting...")
                      : captchaConfigLoading
                        ? t("common.captcha_loading", "Loading...")
                        : paymentMethod === "bank"
                          ? t("tour_detail.submit_pay_bank_transfer", "Submit request & pay by bank transfer")
                          : t("tour_detail.pay_and_book_now", "Pay & book now")}
                </button>
              </form>
            </>
          )}
        </div>

          {/* Sağ: My Trip sticky sidebar (TourRadar tarzı) */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-2xl border border-gray-100 bg-white p-0 shadow-md overflow-hidden">
              <h2 className="bg-gray-100 border-b-2 border-gray-200 px-5 py-3.5 text-xl font-semibold text-gray-900">
                {t("tour_detail.my_trip", "My Trip")}
              </h2>
              <div className="p-5">
            {!hasMounted ? (
              <p className="text-sm text-gray-500">{language === "tr" ? "Yükleniyor..." : "Loading..."}</p>
            ) : hasValidSelection ? (
              <>
                <div className="space-y-4 text-sm">
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("tour_detail.product", "Product")}
                    </span>
                    <p className="mt-0.5 font-medium text-gray-900 line-clamp-2">{displayProductName || "—"}</p>
                  </div>
                  {productStartCity && (
                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {language === "tr" ? "Başlangıç" : "Starts"}
                      </span>
                      <p className="mt-0.5 font-medium text-gray-900">
                        {language === "tr" ? `${productStartCity}’de` : `in ${productStartCity}`}
                      </p>
                      <p className="mt-0.5 text-gray-600">{formattedDepartureDate}</p>
                    </div>
                  )}
                  {productEndCity && (
                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {language === "tr" ? "Bitiş" : "Ends"}
                      </span>
                      <p className="mt-0.5 font-medium text-gray-900">
                        {language === "tr" ? `${productEndCity}’de` : `in ${productEndCity}`}
                      </p>
                      {effectiveDepartureDate !== "-" && tourDurationDays != null && tourDurationDays > 0 && (() => {
                        const start = new Date(effectiveDepartureDate);
                        const end = new Date(start);
                        end.setDate(end.getDate() + tourDurationDays);
                        return (
                          <p className="mt-0.5 text-gray-600">
                            {end.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                  {!productStartCity && !productEndCity && (
                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {t("tour_detail.departure_date", "Departure Date")}
                      </span>
                      <p className="mt-0.5 font-medium text-gray-900">{formattedDepartureDate}</p>
                    </div>
                  )}
                  <div>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("tour_detail.travelers", "Travelers")}
                    </span>
                    <p className="mt-0.5 font-medium text-gray-900">
                      {adults} {adults === 1 ? t("tour_detail.adult", "Adult") : t("tour_detail.adults", "Adults")}
                      {children > 0 && `, ${children} ${children === 1 ? t("tour_detail.child", "Child") : t("tour_detail.children", "Children")}`}
                    </p>
                  </div>
                  {(hasPrice || showFromPrice) && (
                    <div className="border-t border-gray-200 pt-3 space-y-1">
                      {campaignNameForSelectedDate && (
                        <div className="flex items-center gap-2 text-xs font-medium text-amber-700">
                          <span className="rounded bg-amber-100 px-2 py-0.5">{campaignNameForSelectedDate}</span>
                          <span>{language === "tr" ? "uygulandı" : "applied"}</span>
                        </div>
                      )}
                      {partialPaymentActive && paymentModeForSubmit === "partial" && partialScheduleFees ? (
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between text-gray-700">
                            <span>{language === "tr" ? "Tur bedeli" : "Tour price"}</span>
                            <span className="tabular-nums">{formatMoney(estimateTotal)}</span>
                          </div>
                          {partialScheduleFees.feesTotal > 0.009 && (
                            <div className="flex justify-between text-gray-700">
                              <span>
                                {language === "tr"
                                  ? `Vade farkı (${partialScheduleFees.percent.toFixed(2)}%)`
                                  : `Late fees (${partialScheduleFees.percent.toFixed(2)}%)`}
                              </span>
                              <span className="tabular-nums">+{formatMoney(partialScheduleFees.feesTotal)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-alsero-dark-gray border-t border-gray-100 pt-1.5">
                            <span>{language === "tr" ? "Toplam (tur + vade farkı)" : "Subtotal (tour + late fees)"}</span>
                            <span className="tabular-nums">{formatMoney(grandTotalWithLateFees)}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between font-semibold text-alsero-dark-gray">
                          <span>
                            {showFromPrice && !hasPriceFromApi
                              ? language === "tr"
                                ? "Başlangıç fiyatı"
                                : "From"
                              : t("tour_detail.estimated_total", "Estimated total")}
                          </span>
                          <span>{showFromPrice && !hasPriceFromApi ? formatMoney(minFromPrice) : formatMoney(estimateTotal)}</span>
                        </div>
                      )}
                      {paymentMethod === "card" && cardCommissionNowAmount > 0.009 && (
                        <div className="flex justify-between text-xs text-amber-800">
                          <span>
                            {t("payment.card_commission", "Card commission")}
                            {cardCommissionNow?.commission_rate ? ` (${cardCommissionNow.commission_rate}%)` : ""}
                          </span>
                          <span className="tabular-nums">+{formatMoney(cardCommissionNowAmount)}</span>
                        </div>
                      )}
                      {paymentMethod === "card" && cardCommissionNowAmount > 0.009 && (
                          <div className="flex justify-between font-semibold text-alsero-dark-gray border-t border-gray-100 pt-1.5">
                            <span>{language === "tr" ? "Toplam (kredi kartı ile)" : "Total (credit card)"}</span>
                            <span className="tabular-nums">{formatMoney(grandTotalWithCard)}</span>
                          </div>
                        )}
                      {partialPaymentActive && checkoutPayAmount > 0 && checkoutPayAmount + 0.009 < estimateTotal && (
                        <div className="flex justify-between text-xs text-gray-600 pt-1">
                          <span>{language === "tr" ? "Şimdi ödenecek (tur payı)" : "Pay now (tour portion)"}</span>
                          <span className="tabular-nums">{formatMoney(checkoutPayAmount)}</span>
                        </div>
                      )}
                      {partialPaymentActive &&
                        partialScheduleFees &&
                        partialScheduleFees.scheduledWithFees > 0.009 && (
                          <div className="flex justify-between text-xs text-gray-600">
                            <span>{language === "tr" ? "Vade planı (anapara + fark)" : "Scheduled (principal + fees)"}</span>
                            <span className="tabular-nums">{formatMoney(partialScheduleFees.scheduledWithFees)}</span>
                          </div>
                        )}
                      {paymentMethod === "card" && cardPayableAmount != null && cardPayableAmount > 0 && (
                        <div className="flex justify-between text-sm font-medium text-green-700 pt-1 border-t border-gray-100">
                          <span>{t("payment.amount_to_pay_card", "Amount to pay (card)")}</span>
                          <span className="whitespace-nowrap tabular-nums">{formatMoney(cardPayableAmount)}</span>
                        </div>
                      )}
                      {paymentMethod === "bank" && partialPaymentActive && checkoutPayAmount > 0 && (
                        <div className="flex justify-between text-sm font-medium text-green-700 pt-1 border-t border-gray-100">
                          <span>{language === "tr" ? "Şimdi havale tutarı" : "Transfer amount now"}</span>
                          <span className="tabular-nums">{formatMoney(checkoutPayAmount)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                  <a
                    href={getVariable("contact_page_url", `/${language}/contact`) || "#"}
                    className="text-sm font-medium text-alsero-first hover:underline"
                  >
                    {language === "tr" ? "Yardım" : "Help"}
                  </a>
                  <span className="text-gray-300">|</span>
                  <a
                    href={getVariable("contact_page_url", `/${language}/contact`) || "#"}
                    className="text-sm font-medium text-alsero-first hover:underline"
                  >
                    {language === "tr" ? "İletişim" : "Contact Us"}
                  </a>
                  {(getVariable("site_phone", "") || getVariable("contact_phone", "")).trim() && (
                    <>
                      <span className="text-gray-300">|</span>
                      <a
                        href={`tel:${(getVariable("site_phone", "") || getVariable("contact_phone", "")).trim().replace(/\s/g, "")}`}
                        className="text-sm font-medium text-alsero-first hover:underline"
                      >
                        {(getVariable("site_phone", "") || getVariable("contact_phone", "")).trim()}
                      </a>
                    </>
                  )}
                </div>
                <button
                  type="submit"
                  form="book-now-checkout-form"
                  disabled={
                    submitting ||
                    paymentProcessing ||
                    captchaConfigLoading ||
                    !meetsMinGroup ||
                    !meetsMaxGroup ||
                    !meetsDepartureQuota ||
                    (isGroupTourBooking && !selectedDepartureBookable)
                  }
                  className="mt-6 flex w-full items-center justify-center rounded-lg bg-alsero-first px-5 py-3.5 text-base font-semibold text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {paymentProcessing
                    ? t("payment.processing", "Processing...")
                    : submitting
                      ? t("reservation.submitting", "Submitting...")
                      : captchaConfigLoading
                        ? t("common.captcha_loading", "Loading...")
                        : paymentMethod === "bank"
                          ? t("tour_detail.submit_pay_bank_transfer", "Submit request & pay by bank transfer")
                          : t("tour_detail.pay_and_book_now", "Pay & book now")}
                </button>
                <p className="mt-3 text-center text-xs text-gray-500">
                  {t("tour_detail.book_now_continue_hint", "Complete traveler details and payment below, then confirm.")}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  {t("tour_detail.book_now_missing_selection", "Missing booking selection. Please choose a departure date again.")}
                </p>
                <Link
                  href={backToDatesHref}
                  className="mt-4 flex w-full items-center justify-center rounded-lg bg-gray-700 px-5 py-3 text-base font-semibold text-white hover:bg-gray-800"
                >
                  {t("tour_detail.back_to_departure_dates", "Back to Departure Dates")}
                </Link>
              </>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {loginModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-md p-6">
          <h2 id="login-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
            {t("reservation.email_registered_login_title", language === "tr" ? "Bu e-posta kayıtlı – Giriş yapın" : "This email is registered – Log in")}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t("reservation.email_registered_login_hint", language === "tr" ? "Hesabınızla giriş yapın, iletişim bilgileriniz otomatik doldurulacaktır." : "Log in with your account and your contact details will be filled in automatically.")}
          </p>
          <form onSubmit={handleLoginModalSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("reservation.email", "Email")}</label>
              <input
                type="email"
                value={loginModalEmail}
                readOnly
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.password", "Password")}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/30 focus:border-alsero-first"
                placeholder={language === "tr" ? "Şifre" : "Password"}
                required
                autoComplete="current-password"
              />
            </div>
            {loginError && <p className="text-sm text-red-600">{loginError}</p>}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setLoginModalOpen(false); setLoginPassword(""); setLoginError(null); setError(null); }}
                className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                disabled={loginLoading}
                className="flex-1 px-4 py-2.5 rounded-lg bg-alsero-first text-white font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {loginLoading ? t("auth.logging_in", "Logging in...") : t("auth.login.submit", "Log in")}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {selectedAgreement && (
      <AgreementModal
        isOpen={isAgreementModalOpen}
        onClose={() => {
          setIsAgreementModalOpen(false);
          setSelectedAgreement(null);
        }}
        agreementSlug={selectedAgreement.slug}
        language={language}
        agreementId={selectedAgreement.id}
        isRequired={selectedAgreement.is_required}
        onAccept={handleAgreementAccept}
      />
    )}
    </>
  );
}
